import React, { useState, useEffect, useCallback, useRef } from 'react';
import { capitalizeWords, formatDateTime, formatDate, formatDateLong, staffName } from '../utils/format';
import { useParams, useNavigate } from 'react-router-dom';
import { candidateAPI, configAPI, authAPI, API_URL } from '../services/api';
import { hasFullAccess, departmentForPosition } from '../utils/roles';
import Navigation from '../components/Navigation';
import ConfirmDialog from '../components/ConfirmDialog';
import AlertDialog from '../components/AlertDialog';
import EmailPreviewModal from '../components/EmailPreviewModal';
import CandidateDocumentFrame from '../components/CandidateDocumentFrame';
import TrainingLogModal from '../components/TrainingLogModal';
import './CandidateDetail.css';

// Every CandidateStatus, in pipeline order, with the labels the candidate list
// already uses — keep the two in step. This is the manual override the backend
// reserves for HR: an explicit status in PATCH /candidates/{id} is applied
// unguarded, so it can go backwards. Nothing else in the app could set an
// arbitrary status, which left a wrongly-clicked "Mark as Recruited" with no way
// back (candidate 26, 2026-09-17 — it took an API call to undo).
const STATUS_OPTIONS = [
  ['new', 'New'],
  ['cv_received', 'CV Received'],
  ['background_check', 'Background Check'],
  ['phone_interview_scheduled', 'Phone Interview Scheduled'],
  ['phone_interview_completed', 'Phone Interview Completed'],
  ['in_person_interview_scheduled', 'In-Person Interview Scheduled'],
  ['in_person_interview_completed', 'In-Person Interview Completed'],
  ['trial_day_scheduled', 'Trial Day Scheduled'],
  ['trial_day_completed', 'Trial Day Completed'],
  ['offer_sent', 'Job Offer Sent'],
  ['offer_accepted', 'Offer Accepted'],
  ['offer_declined', 'Job Offer Declined'],
  ['references_requested', 'References Requested'],
  ['reference_received', 'Reference Received'],
  ['referee_details_received', 'Referee Details Received'],
  ['documents_email_sent', 'Documents Email Sent'],
  ['all_starter_docs_received', 'All Starter Docs Received'],
  ['contract_email_sent', 'Contract Email Sent'],
  ['awaiting_contract_signature', 'Awaiting Contract Signature'],
  ['candidate_signed', 'Candidate Signed Contract'],
  ['contract_signed', 'Contract Signed'],
  ['onboarding_checklist_complete', 'Onboarding Checklist Complete'],
  ['awaiting_induction_signatures', 'Awaiting Induction Signatures'],
  ['induction_folder_complete', 'Induction Folder Complete'],
  ['recruited', 'Recruited !'],
  ['rejected', 'Rejected'],
];

// The end-of-road statuses, mirroring TERMINAL_STATUSES in
// backend/app/models/candidate.py. Reaching one archives the candidate; leaving
// one brings them back. Kept in one place because four separate checks used to
// spell the list out by hand, and adding offer_declined would have meant finding
// every one of them.
const TERMINAL_STATUSES = ['recruited', 'rejected', 'offer_declined'];
// The two that ask "why?" in their confirmation (OUTCOME_COMMENT_STATUSES in
// api/candidates.py); the answer shows under Current Status.
const OUTCOME_COMMENT_STATUSES = ['rejected', 'offer_declined'];

const statusLabel = (value) =>
  (STATUS_OPTIONS.find(([status]) => status === value) || [null, value])[1];

const formatBytes = (n) =>
  n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`;

// Part 4 contract-details table styling. Borders use the Part 4 pane colour.
// Part 1 (Interview) frame + title. Pascal's pick, 2026-10-03; the other
// panels keep their colour inline where the frame is drawn.
// The value lives in index.css (--part1-color).
const PART1A_COLOR = 'var(--part1-color)';
const PART3_COLOR = '#28a745';
const PART3_ROW_HEIGHT = '40px';
const PART3_TD_LABEL = {
  padding: '4px 14px',
  border: `1px solid ${PART3_COLOR}`,
  fontWeight: '500',
  color: 'var(--color-text-light)',
  width: '30%',
  height: PART3_ROW_HEIGHT,
  verticalAlign: 'middle',
};
const PART3_TD_VALUE = {
  padding: '4px 14px',
  border: `1px solid ${PART3_COLOR}`,
  height: PART3_ROW_HEIGHT,
  verticalAlign: 'middle',
};

// Durable 4-state send-status line shown under every email send button. The
// send is async and only confirmed in the Celery task, so we never show success
// optimistically: `sentDate` (confirmed) wins, then `failedDate` (persisted
// failure), then `attemptedDate` (in-flight), else "Not yet sent". Mirrors the
// reference implementation shipped for the Part 3 documents email.
const SEND_STATUS_BASE = { fontSize: '12px', textAlign: 'center' };
function SendStatusSubtitle({ sentDate, failedDate, attemptedDate, sentLabel = 'Confirmed as sent', glow = false }) {
  // glow: the next move is someone else's, so this line pulses instead of a button.
  const cls = glow ? 'next-glow-text next-glow-tint' : undefined;
  if (sentDate) {
    return (
      <span className={cls} style={{ ...SEND_STATUS_BASE, color: 'var(--color-text-muted)' }}>
        {formatDateTime(sentDate)} · {sentLabel}
      </span>
    );
  }
  if (failedDate) {
    return (
      <span className={cls} style={{ ...SEND_STATUS_BASE, color: '#dc2626', fontWeight: '600' }}>
        ✗ Last send failed {formatDateTime(failedDate)} — not delivered
      </span>
    );
  }
  if (attemptedDate) {
    return (
      <span className={cls} style={{ ...SEND_STATUS_BASE, color: '#b45309' }}>
        Sending… (attempted {formatDateTime(attemptedDate)})
      </span>
    );
  }
  return (
    <span className={cls} style={{ ...SEND_STATUS_BASE, color: 'var(--color-text-muted)' }}>
      Not yet sent
    </span>
  );
}

// The Accounts Administrator is emailed a new-starter pack once the personnel record,
// HMRC starter checklist and fully-executed contract all exist. This marks those three
// documents as having gone to her, so HR can see at a glance that payroll has them.
//
// The name is NOT hardcoded: it comes from the backend (settings.ACCOUNTS_NAME, served
// by GET /api/app-config), so when the role changes hands the UI follows automatically
// and can never show a departed employee's name. Falls back to the role title if the
// config request hasn't landed yet.
//
// Driven by accounts_notified_date, which the backend writes ONLY on a confirmed send —
// so this tick can never claim an email that did not actually go out.
// The label of a "sent" button that still works: it reads "Send again" while
// hovered, so nobody re-sends thinking they are looking at a status. Both labels
// sit in the same grid cell, so the button keeps the wider one's size and does
// not jump on hover (swap in App.css: .resend-label).
function ResendLabel({ label, again = 'Send again' }) {
  return (
    <span className="resend-label">
      <span className="resend-label-sent">{label}</span>
      <span className="resend-label-again" aria-hidden="true">{again}</span>
    </span>
  );
}

function AccountsSentTick({ notifiedDate, accountsName }) {
  if (!notifiedDate) return null;
  return (
    <span
      title={`Sent to the Accounts Administrator ${formatDateTime(notifiedDate)}`}
      style={{ fontSize: '12px', color: '#5cb85c', fontWeight: '600', whiteSpace: 'nowrap', marginLeft: '10px' }}
    >
      ✓ {staffName(accountsName) || 'Accounts Administrator'} emailed
    </span>
  );
}

function CandidateDetail({ onLogout, onFeedback }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReferenceForm, setShowReferenceForm] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showJobOfferModal, setShowJobOfferModal] = useState(false);
  const [scheduleType, setScheduleType] = useState('');
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [scheduleDuration, setScheduleDuration] = useState(60);
  const [expandedSections, setExpandedSections] = useState({
    contact: true
  });
  const [jobOfferForm, setJobOfferForm] = useState({
    hours_per_week: '40',
    hours_per_shift: '8',
    annual_salary: '45000',
    hourly_salary: '21.63',
    hours_of_work: '',
    hours_of_work_edited: false,
  });
  // Every position's standard "Hours of work" contract wording, with the
  // {{ hours_per_week }} token left in (services/hours_of_work.py on the
  // backend). null until loaded — and if it never loads, the Hours of work box
  // is simply not shown and the field is not sent, so nothing on the record is
  // touched by a form that could not see it.
  const [hoursOfWorkDefaults, setHoursOfWorkDefaults] = useState(null);
  // Candidate Information shows the bespoke contract wording folded to one line.
  const [amendedWordingOpen, setAmendedWordingOpen] = useState(false);
  useEffect(() => {
    candidateAPI.hoursOfWorkDefaults()
      .then(setHoursOfWorkDefaults)
      .catch(() => setHoursOfWorkDefaults(null));
  }, []);
  // Mirrors format_hours on the backend: 40 -> "40", 11.25 -> "11.25", '' -> TBD.
  const formatHoursFigure = (hours) => {
    if (hours === '' || hours == null) return 'TBD';
    const n = Number(hours);
    return Number.isNaN(n) ? String(hours) : String(n);
  };
  const standardHoursOfWork = (position, hours) => {
    const template = hoursOfWorkDefaults?.[String(position || '').toLowerCase()];
    return template ? template.split('{{ hours_per_week }}').join(formatHoursFigure(hours)) : '';
  };
  const [referenceForm, setReferenceForm] = useState({
    reference_name: '',
    reference_email: '',
    reference_phone: '',
    reference_organization: '',
    reference_position: '',
    candidate_position: '',
    relationship_to_candidate: '',
    employment_start_date: '',
    employment_end_date: '',
  });
  const [showNightWorkerModal, setShowNightWorkerModal] = useState(false);
  const [showMedicalModal, setShowMedicalModal] = useState(false);
  const [showHmrcModal, setShowHmrcModal] = useState(false);
  const [showPersonalRecordModal, setShowPersonalRecordModal] = useState(false);
  const [showUnumModal, setShowUnumModal] = useState(false);
  const [showIdDocumentModal, setShowIdDocumentModal] = useState(false);
  const [showQualificationModal, setShowQualificationModal] = useState(false);
  const [showCpdRecordModal, setShowCpdRecordModal] = useState(false);
  const [rcvsVerifying, setRcvsVerifying] = useState(false);
  const [progressing, setProgressing] = useState(false); // "Progress to RVN" in flight
  const [showRcvsResultsModal, setShowRcvsResultsModal] = useState(false);
  const [rcvsVerificationFailed, setRcvsVerificationFailed] = useState(false);

  // Claire notification confirmation
  const [showClaireConfirmModal, setShowClaireConfirmModal] = useState(false);
  const [pendingStartDate, setPendingStartDate] = useState(null);
  const [originalStartDate, setOriginalStartDate] = useState(null);

  // PART 5: Pre-start onboarding checklist state
  const [onboardingData, setOnboardingData] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [expandedCompletedTasks, setExpandedCompletedTasks] = useState({});

  // PART 6: Induction Folder state
  const [inductionData, setInductionData] = useState(null);
  // Part 6 Training Log table (filled in the app, signed at the end of training)
  const [trainingLogOpen, setTrainingLogOpen] = useState(false);
  const [inductionLoading, setInductionLoading] = useState(false);
  const [inductionGeneratingDocs, setInductionGeneratingDocs] = useState({});
  const [verifyingInductionRequests, setVerifyingInductionRequests] = useState(false);

  // Part 4: Contract generation state
  const [generatingContract, setGeneratingContract] = useState(false);

  // Candidate Information inline edit state
  const [editingInfo, setEditingInfo] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [infoForm, setInfoForm] = useState({});

  // Job Descriptions state
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [jobDescriptionsLoading, setJobDescriptionsLoading] = useState(false);
  // Accounts Administrator's display name, from the backend — never hardcoded here.
  const [accountsName, setAccountsName] = useState('');
  // Part 4 eSignature provider, from the backend. "signwell": Generate Contract
  // sends the signature request itself (no Doc tab, no manual Drive step) and
  // the sweep reads SignWell for completion. Anything else: the Google flow.
  const [esignProvider, setEsignProvider] = useState('google');
  const [signwellTestMode, setSignwellTestMode] = useState(false);
  // Test-mode SignWell requests send no email — the direct signing links from
  // the last Generate are the only way to sign, so they are shown on the page.
  const [signwellSigningUrls, setSignwellSigningUrls] = useState(null);
  const usingSignWell = esignProvider === 'signwell';

  // A progression: a recruited student/trainee nurse re-hired as an RVN on a NEW
  // record linked to the old one (POST /{id}/progress). Same person, so the page
  // asks only for what changes — Part 1 is the interview alone, Part 2 does not
  // exist (the contract is the offer), Part 3 is the RCVS registration and proof
  // of qualification, Part 5 four tasks, Part 6 the dispensary training log.
  const isProgression = !!candidate?.is_progression;
  const openProgression = candidate?.progressed_to
    && !['rejected', 'offer_declined'].includes(candidate.progressed_to.status);
  const canProgress = !!candidate && !isProgression && candidate.status === 'recruited'
    && ['student_nurse', 'trainee_nurse'].includes((candidate.position_applied || '').toLowerCase())
    && !openProgression;
  // Trainee Nurse and Cleaner get the offer letter without a reference request
  // (EmailService.send_offer_email), so Part 2 has nothing to wait for from referees.
  const requiresReferences = !isProgression
    && !['trainee_nurse', 'cleaner'].includes((candidate?.position_applied || '').toLowerCase());
  // Part 2 is done when the offer is accepted and, where references are asked
  // for, at least one has come back. It used to demand a referee record as well,
  // which the no-reference positions never have, so it could never complete.
  const part2Complete = !!candidate?.offer_accepted_date
    && (!requiresReferences || (candidate?.references || []).some(r => r.response_received_date));

  // Part 6 "complete" means the policy documents are signed; the Training Log
  // is kept out of that rule on purpose (training runs for weeks afterwards).
  // While a log is still unsigned the section must say so, and must not fold
  // itself away with the log's "In progress" row inside it — Sophie Kanikuru's
  // page read "Completed" with 0 of 37 rows ticked (2026-10-08).
  const pendingTrainingLog = inductionData?.documents?.find(
    doc => doc.kind === 'training_log' && !doc.status?.signed_date
  ) || null;
  const inductionSettled = !!inductionData?.completed && !pendingTrainingLog;
  // Progress counts the Training Log as one more document to sign when the
  // position has one, so a signed set of policies reads "4 / 5 (80%)", not 100%.
  // Display only: the server's stats (and its completion rule) stay policy-only.
  const trainingLogDoc = inductionData?.documents?.find(doc => doc.kind === 'training_log') || null;
  // Once the policy documents are all signed, the Training Log is the only
  // thing left to do in Part 6, so its card goes to the top of the list.
  const orderedInductionDocs = (inductionData?.documents || []).slice().sort((a, b) => {
    if (!inductionData?.completed || !pendingTrainingLog) return 0;
    return (b.kind === 'training_log') - (a.kind === 'training_log');
  });
  const inductionProgress = inductionData ? (() => {
    const total = inductionData.stats.requires_esignature_count + (trainingLogDoc ? 1 : 0);
    const signed = inductionData.stats.signed + (trainingLogDoc?.status?.signed_date ? 1 : 0);
    return { total, signed, percent: total > 0 ? Math.round((signed / total) * 100) : 100 };
  })() : null;
  // The viewer's role, from /me. All-access roles have personal Google Drive
  // access and open documents straight from Drive (they need the real Doc to
  // launch eSignature requests). Everyone else — and anyone whose role has not
  // loaded yet — is served the document through the backend instead, because
  // Drive sharing cannot follow department scoping. See openCandidateDocument.
  const [viewerRole, setViewerRole] = useState(null);
  const driveDirect = hasFullAccess(viewerRole);

  // Confirm Dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    message: '',
    buttonPosition: null,
    onConfirm: null
  });
  const confirmPromiseRef = useRef(null);

  // Helper function to show confirm dialog and return a promise
  // Resolves true/false — or, when options.commentLabel is given (the dialog
  // then shows a comment box), { confirmed, comment }.
  const showConfirm = (message, event, options = {}) => {
    // Get button position from the click event
    const buttonPosition = event?.target?.getBoundingClientRect() || null;

    return new Promise((resolve) => {
      confirmPromiseRef.current = options.commentLabel
        ? (confirmed, comment = '') => resolve({ confirmed, comment })
        : resolve;
      setConfirmDialog({
        isOpen: true,
        message,
        buttonPosition,
        commentLabel: options.commentLabel || null,
      });
    });
  };

  const handleConfirmDialogConfirm = (comment) => {
    if (confirmPromiseRef.current) {
      confirmPromiseRef.current(true, comment);
      confirmPromiseRef.current = null;
    }
    setConfirmDialog({ isOpen: false, message: '', buttonPosition: null });
  };

  const handleConfirmDialogCancel = () => {
    if (confirmPromiseRef.current) {
      confirmPromiseRef.current(false);
      confirmPromiseRef.current = null;
    }
    setConfirmDialog({ isOpen: false, message: '', buttonPosition: null });
  };

  // Alert Dialog state
  const [alertDialog, setAlertDialog] = useState({
    isOpen: false,
    message: '',
    type: 'info' // 'success', 'error', 'info'
  });

  // Email Preview Modal state
  const [emailPreviewModal, setEmailPreviewModal] = useState({
    isOpen: false,
    emailData: null,
    emailType: null,
    onSendCallback: null
  });

  // Scroll preservation for component re-renders
  const preservedScrollPosition = useRef(null);

  // Helper function to show alert dialog and return a promise
  const showAlert = (message, type = 'info') => {
    // Save scroll position before any action
    preservedScrollPosition.current = {
      x: window.scrollX || window.pageXOffset,
      y: window.scrollY || window.pageYOffset
    };

    return new Promise((resolve) => {
      setAlertDialog({
        isOpen: true,
        message,
        type
      });
      // Resolve immediately since this is just a notification
      // The actual close will be handled by the OK button
      resolve();
    });
  };

  const handleAlertDialogClose = () => {
    setAlertDialog({ isOpen: false, message: '', type: 'info' });

    // Restore scroll position after a short delay to ensure React has finished re-rendering
    setTimeout(() => {
      if (preservedScrollPosition.current) {
        window.scrollTo(preservedScrollPosition.current.x, preservedScrollPosition.current.y);
        preservedScrollPosition.current = null;
      }
    }, 50);
  };


  // Selectable values for the Candidate Information edit form (must match the
  // JobPosition / CandidateSource enums in backend/app/models/candidate.py)
  const POSITION_OPTIONS = [
    'office', 'cleaner', 'receptionist', 'rvn', 'rvn_night_nurse', 'student_nurse',
    'trainee_nurse', 'veterinary_surgeon', 'ooh_veterinary_surgeon', 'zero_hours_vet'
  ];
  const SOURCE_OPTIONS = ['website', 'indeed', 'word_of_mouth', 'writtle_college', 'see_practice', 'recruiter'];


  const startEditInfo = () => {
    setInfoForm({
      first_name: candidate.first_name || '',
      last_name: candidate.last_name || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      position_applied: candidate.position_applied || '',
      source: candidate.source || '',
      status: candidate.status || '',
      hours_per_week: candidate.hours_per_week ?? '',
      hours_per_shift: candidate.hours_per_shift ?? '',
      annual_salary: candidate.annual_salary ?? '',
      hourly_salary: candidate.hourly_salary || '',
      hours_of_work: candidate.hours_of_work || standardHoursOfWork(candidate.position_applied, candidate.hours_per_week),
      hours_of_work_edited: !!candidate.hours_of_work,
      temporary_reason: candidate.temporary_reason || '',
    });
    setEditingInfo(true);
  };

  const handleInfoChange = (field, value) => {
    setInfoForm(prev => {
      const next = { ...prev, [field]: value };
      // Same rule as the offer form: the Hours of work box follows the position
      // and hours until someone edits it.
      if ((field === 'hours_per_week' || field === 'position_applied') && !next.hours_of_work_edited) {
        next.hours_of_work = standardHoursOfWork(next.position_applied, next.hours_per_week);
      }
      if (field === 'hours_of_work') {
        next.hours_of_work_edited = value !== standardHoursOfWork(next.position_applied, next.hours_per_week);
      }
      return next;
    });
  };

  const resetInfoHoursOfWork = () => {
    setInfoForm(prev => ({
      ...prev,
      hours_of_work: standardHoursOfWork(prev.position_applied, prev.hours_per_week),
      hours_of_work_edited: false,
    }));
  };

  const saveInfo = async (event) => {
    if (!infoForm.first_name.trim() || !infoForm.last_name.trim() || !infoForm.email.trim()) {
      showAlert('First name, last name and email are required', 'error');
      return;
    }

    // Moving to Recruited or Rejected from here archives the candidate just as the
    // buttons do, so it asks in the same way. Only on an actual change: re-saving
    // the status someone already has is not a decision.
    const statusChanged = infoForm.status && infoForm.status !== candidate.status;
    const archives =
      statusChanged &&
      TERMINAL_STATUSES.includes(infoForm.status);
    if (archives) {
      const name = `${infoForm.first_name} ${infoForm.last_name}`
        .replace(/\s+/g, ' ')
        .trim();
      const confirmed = await showConfirm(
        `Set ${name} to ${statusLabel(infoForm.status)}?\n\nThis archives them, so they leave your default candidate list. They stay fully editable, and you can change the status back if this was a mistake.`,
        event
      );
      if (!confirmed) return;
    }
    // Hours are FLOAT columns (part-time weeks and half shifts are real: 22.5 hours,
    // 7.5-hour shifts), so they must not go through parseInt — that silently wrote 22
    // for a 22.5-hour week, and those numbers drive the pro-rata holiday calculation
    // and the generated contract. Annual salary stays whole pounds: the column is an
    // integer and the offer endpoint rejects pence outright rather than rounding a
    // contractual figure.
    const toNumber = (v) => (v === '' || v === null ? null : Number(v));
    const toInt = (v) => (v === '' || v === null ? null : parseInt(v, 10));
    const payload = {
      first_name: infoForm.first_name.trim(),
      last_name: infoForm.last_name.trim(),
      email: infoForm.email.trim(),
      phone: infoForm.phone.trim() || null,
      position_applied: infoForm.position_applied,
      source: infoForm.source,
      status: infoForm.status,
      hours_per_week: toNumber(infoForm.hours_per_week),
      hours_per_shift: toNumber(infoForm.hours_per_shift),
      annual_salary: toInt(infoForm.annual_salary),
      hourly_salary: infoForm.hourly_salary.trim() || null,
    };
    // Only when the box could be shown; the backend keeps the text only if it
    // differs from the standard wording for the saved position and hours.
    if (hoursOfWorkDefaults) payload.hours_of_work = infoForm.hours_of_work;
    if (candidate.employment_type === 'TEMPORARY') payload.temporary_reason = infoForm.temporary_reason;
    setSavingInfo(true);
    try {
      await candidateAPI.update(id, payload);
      setEditingInfo(false);
      await loadCandidate(true);
      // Say when the save archived or un-archived them, rather than letting the
      // candidate quietly leave (or rejoin) the default list.
      const leftTerminal =
        statusChanged &&
        TERMINAL_STATUSES.includes(candidate.status) &&
        !archives;
      showAlert(
        archives
          ? 'Saved. Candidate archived — still editable, and reachable from Archived.'
          : leftTerminal
          ? 'Saved. Candidate returned to your active list.'
          : 'Candidate information updated',
        'success'
      );
    } catch (error) {
      const detail = error?.response?.data?.detail;
      showAlert(typeof detail === 'string' ? detail : 'Failed to update candidate information', 'error');
    } finally {
      setSavingInfo(false);
    }
  };

  // Helper function to check if all required documents are received
  const areAllDocumentsReceived = (candidate) => {
    if (!candidate) return false;
    // A progression asks for two things only (status_flow.all_starter_docs_received).
    if (candidate.is_progression) {
      return !!(candidate.rcvs_verified && candidate.proof_of_qualification_received);
    }

    const baseDocsReceived = candidate.medical_form_received &&
                             candidate.hmrc_form_received &&
                             candidate.personal_record_received &&
                             candidate.id_document_received;

    // Positions that must also supply proof of qualification and a CPD record.
    // MUST match EmailService._requires_professional_docs on the backend and the
    // render condition further down this file. It previously listed five positions
    // that do not exist (ooh_veterinary_nurse, clinical_coach, head_of_clinic_nurse,
    // head_of_clinical_procedures, deputy_head_nurse — the last two are user ROLES,
    // not job positions) while omitting two that do, so a Zero Hours Vet or RVN
    // Night Nurse was shown the qualification and CPD boxes but counted as complete
    // without them.
    const professionalPositions = [
      'veterinary_surgeon',
      'ooh_veterinary_surgeon',
      'zero_hours_vet',
      'rvn',
      'rvn_night_nurse'
    ];
    if (professionalPositions.includes(candidate.position_applied.toLowerCase())) {
      return baseDocsReceived && candidate.proof_of_qualification_received && candidate.cpd_record_received;
    }

    return baseDocsReceived;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const loadCandidate = useCallback(async (preserveScroll = false) => {
    // Save scroll position if requested
    const savedScroll = preserveScroll ? {
      x: window.scrollX || window.pageXOffset,
      y: window.scrollY || window.pageYOffset
    } : null;

    try {
      setLoading(true);
      const data = await candidateAPI.get(id);
      setCandidate(data);
      setOriginalStartDate(data.start_date); // Store original start date for comparison
    } catch (error) {
      console.error('Error loading candidate:', error);
      showAlert('Failed to load candidate', 'error');
      navigate('/candidates');
    } finally {
      setLoading(false);

      // Restore scroll position after state updates complete
      if (savedScroll) {
        setTimeout(() => {
          window.scrollTo(savedScroll.x, savedScroll.y);
        }, 100);
      }
    }
  }, [id, navigate]);

  // Fetch job descriptions list from Google Drive folder
  const fetchJobDescriptions = useCallback(async () => {
    try {
      setJobDescriptionsLoading(true);
      const response = await fetch(`${API_URL}/api/candidates/job-descriptions/list`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setJobDescriptions(data);
      } else {
        console.error('Failed to fetch job descriptions');
      }
    } catch (error) {
      console.error('Error fetching job descriptions:', error);
    } finally {
      setJobDescriptionsLoading(false);
    }
  }, []);

  // Handle job description selection
  const handleJobDescriptionChange = async (event) => {
    const fileId = event.target.value;

    if (!fileId) {
      return;
    }

    try {
      await candidateAPI.update(id, { job_description_file_id: fileId });
      await loadCandidate(true); // Reload candidate with scroll preservation
      showAlert('Job description selected', 'success');
    } catch (error) {
      console.error('Error updating job description:', error);
      showAlert('Failed to update job description', 'error');
    }
  };

  // Handle sending job description email
  // Poll the backend for the REAL outcome of a queued send task so a failed
  // send surfaces as an error instead of a silent non-update. Shared by every
  // send button. Returns true=sent, false=failed, null=unknown/timeout.
  const pollSendOutcome = async (taskId) => {
    if (!taskId) return null;
    for (let i = 0; i < 40; i++) { // ~60s max (downloads + up to 3 send retries)
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const status = await candidateAPI.getTaskStatus(id, taskId);
        if (status.ready) return status.successful;
      } catch (e) { /* transient; keep polling */ }
    }
    return null;
  };

  const handleSendJobDescriptionEmail = async (event) => {
    if (!candidate.job_description_file_id) {
      showAlert('Please select a job description first', 'error');
      return;
    }

    if (!await showConfirm('Send job description email to candidate?', event)) {
      return;
    }

    try {
      const resp = await candidateAPI.api.post(`/api/candidates/${id}/send-job-description`);
      if (resp?.data?.in_flight) {
        showAlert('A job description email is already being sent to this candidate — wait for it to finish (or report a failure) before sending again.', 'info');
        loadCandidate(true);
        return;
      }
      showAlert('Sending job description email…', 'info');

      // The send runs in the background; poll for the REAL outcome so a failure
      // surfaces as an error instead of a silent (optimistic) success.
      const outcome = await pollSendOutcome(resp?.data?.task_id);
      if (outcome === true) {
        showAlert(`✓ Job description email sent to ${candidate.email}`, 'success');
      } else if (outcome === false) {
        showAlert('✗ Job description email FAILED to send — nothing was delivered to the candidate. Please try again.', 'error');
      } else {
        showAlert('Job description email is still sending. Refresh in a moment — if the button has not turned green, it did not send.', 'info');
      }
      loadCandidate(true);
    } catch (error) {
      console.error('Error sending job description email:', error);
      showAlert('Failed to send job description email', 'error');
    }
  };

  useEffect(() => {
    loadCandidate();
    fetchJobDescriptions(); // Fetch job descriptions when component mounts
  }, [loadCandidate, fetchJobDescriptions]);

  // Who the Accounts pack goes to. Read from the backend so the tick follows
  // settings.ACCOUNTS_NAME if the role ever changes hands. A failure here is not
  // worth surfacing — the tick just falls back to the role title.
  useEffect(() => {
    configAPI.get()
      .then((cfg) => {
        setAccountsName(cfg.accounts_name || '');
        setEsignProvider(cfg.esignature_provider || 'google');
        setSignwellTestMode(Boolean(cfg.signwell_test_mode));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    authAPI.me().then((me) => setViewerRole(me.role)).catch(() => {/* interceptor handles 401 */});
  }, []);

  // The view panes (form previews, RCVS results, the night-worker assessment)
  // are iframes and new tabs, so their credential rides in the URL. It is a
  // 30-minute token scoped to THIS candidate's panes (candidateAPI.paneToken),
  // never the login token — URLs are logged. Minted when a pane opens and
  // re-minted once it is 20 minutes old, so a long-open page keeps working.
  const [paneAuth, setPaneAuth] = useState('');
  const paneAuthMinted = useRef(0);
  const ensurePaneAuth = useCallback(async () => {
    if (paneAuth && Date.now() - paneAuthMinted.current < 20 * 60 * 1000) return paneAuth;
    const token = await candidateAPI.paneToken(id);
    paneAuthMinted.current = Date.now();
    setPaneAuth(token);
    return token;
  }, [id, paneAuth]);
  useEffect(() => {
    setPaneAuth('');
    paneAuthMinted.current = 0;
  }, [id]);
  const anyPaneOpen = showNightWorkerModal || showMedicalModal || showHmrcModal
    || showPersonalRecordModal || showUnumModal || showRcvsResultsModal;
  useEffect(() => {
    if (anyPaneOpen) ensurePaneAuth().catch((e) => console.error('Pane token:', e));
  }, [anyPaneOpen, ensurePaneAuth]);
  const paneUrl = (path) => (paneAuth ? `${API_URL}/api/candidates/${id}/${path}?auth=${paneAuth}` : undefined);

  // Open one of the candidate's Drive documents in a new tab. All-access roles
  // go straight to Drive (directUrl). Scoped roles have no Drive access, so the
  // file comes through the backend as a blob. The tab is opened synchronously,
  // inside the click, and pointed at the blob once it arrives — opening it after
  // the await would be treated as a pop-up and blocked.
  // The folder is found by name in Drive (ids are not stored), so look it up on
  // click. The tab is opened first, inside the click, or the popup blocker eats it.
  const openDriveFolder = async () => {
    const tab = window.open('', '_blank');
    if (tab) tab.document.write('<p style="font-family: sans-serif; padding: 24px;">Opening folder…</p>');
    try {
      const { data } = await candidateAPI.getDriveFolder(candidate.id);
      if (tab) tab.location.href = data.url;
      else window.open(data.url, '_blank');
    } catch (err) {
      if (tab) tab.close();
      showAlert(
        err.response?.status === 404
          ? 'This candidate has no Documents folder yet. It is created when the first document is saved.'
          : 'The Documents folder could not be opened. Please try again.',
        'error'
      );
    }
  };

  const openCandidateDocument = async (kind, directUrl, docKey = null) => {
    if (driveDirect) {
      window.open(directUrl, '_blank');
      return;
    }
    const tab = window.open('', '_blank');
    if (tab) tab.document.write('<p style="font-family: sans-serif; padding: 24px;">Loading document…</p>');
    try {
      const data = await candidateAPI.fetchDocumentFile(candidate.id, kind, docKey);
      const url = URL.createObjectURL(data);
      if (tab) tab.location.href = url;
      else window.location.href = url;
      // Long enough for the tab to load it; the blob is then held by that tab.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (tab) tab.close();
      showAlert(
        err.response?.status === 403
          ? 'You do not have access to this document.'
          : 'This document could not be opened. Please try again.',
        'error'
      );
    }
  };

  // Auto-collapse Parts when they become completed
  useEffect(() => {
    if (candidate) {
      setExpandedSections(prev => {
        const newExpandedSections = { ...prev };

        // Part 2 - References
        if (part2Complete) {
          newExpandedSections.references = undefined;
        }

        // Part 3 - New Starter Docs
        if (areAllDocumentsReceived(candidate)) {
          newExpandedSections.newStarterDocs = undefined;
        }

        // Part 4 - Contract Letter (collapse once the candidate has signed)
        if (candidate.candidate_signed_date) {
          newExpandedSections.contractLetter = undefined;
        }

        return newExpandedSections;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.offer_accepted_date, candidate?.references?.length,
      candidate?.medical_form_received, candidate?.hmrc_form_received,
      candidate?.personal_record_received, candidate?.id_document_received,
      candidate?.proof_of_qualification_received, candidate?.cpd_record_received,
      candidate?.candidate_signed_date]);

  const handleStatusUpdate = async (newStatus, event) => {
    // Every terminal status archives the candidate, and each is a decision about
    // a person rather than a step in a process, so none should be one unguarded
    // click. Candidate 26 was marked Recruited 22 seconds after being created, and
    // undoing it took two people and a day.
    const archives = TERMINAL_STATUSES.includes(newStatus);

    // Already there: the button is a state label ("Recruited !"), not a toggle, so
    // there is nothing to send. It used to re-PATCH the same status, which the
    // server treated as a fresh arrival at a terminal status and used to re-archive
    // a candidate who had just been deliberately restored.
    if (candidate.status === newStatus) return;

    // Rejected / Offer Declined also ask why: an optional comment saved with the
    // decision (who and when are stamped server-side).
    const asksReason = OUTCOME_COMMENT_STATUSES.includes(newStatus);
    let outcomeComment = '';

    if (archives) {
      const name = `${candidate.first_name} ${candidate.last_name}`
        .replace(/\s+/g, ' ')
        .trim();
      const message = `Set ${name} to ${statusLabel(newStatus)}?\n\nThis archives them, so they leave your default candidate list. They stay fully editable, and you can change the status back if this was a mistake.`;
      if (asksReason) {
        const result = await showConfirm(message, event, { commentLabel: 'Reason or comment (optional)' });
        if (!result.confirmed) return;
        outcomeComment = result.comment;
      } else if (!await showConfirm(message, event)) {
        return;
      }
    }

    if (asksReason) {
      // Rejected / Offer Declined: nothing is applied yet. The email modal
      // concludes it — send, or apply without emailing — or Cancel leaves the
      // candidate exactly as they were.
      await openOutcomeEmail(newStatus, { pending: { status: newStatus, outcome_comment: outcomeComment } });
      return;
    }

    try {
      await candidateAPI.update(id, { status: newStatus });
      loadCandidate(true);
      // A terminal status archives the candidate server-side, which takes them out
      // of the default list — say so rather than letting them silently vanish.
      showAlert(
        archives
          ? 'Status updated. Candidate archived — still editable, and reachable from Archived.'
          : 'Status updated successfully!',
        'success'
      );
    } catch (error) {
      console.error('Error updating status:', error);
      showAlert('Failed to update status', 'error');
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await candidateAPI.restore(id);
      await loadCandidate(true);
      showAlert('Candidate restored to the active list', 'success');
    } catch (error) {
      console.error('Error restoring candidate:', error);
      showAlert('Failed to restore candidate', 'error');
    } finally {
      setRestoring(false);
    }
  };

  // Rejection / Offer Declined email — the "we'd like to keep your details on
  // record unless you'd rather we didn't" line. Offered right after the status is
  // set, and again from the archived strip (past rejections, retries). Unlike
  // every other app email it is sent FROM THE LOGGED-IN USER, which the preview's
  // From line shows; the backend refuses it for any other status.
  const OUTCOME_EMAIL_LABEL = { rejected: 'rejection email', offer_declined: 'offer-declined email' };
  const senderLabel = (email) => email
    ? email.split('@')[0].split('.').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : '';
  // `pending` = { status, outcome_comment } when the decision has NOT been applied
  // yet: the modal is where it concludes. "Send email" applies it and sends,
  // "Reject without emailing" applies it only, Cancel changes nothing. The status
  // used to be set before the modal opened — one mis-click rejected and archived
  // candidate 21 before anyone could stop it (2026-10-09).
  const openOutcomeEmail = async (statusValue, { pending = null } = {}) => {
    const label = OUTCOME_EMAIL_LABEL[statusValue] || 'email';
    const verb = statusValue === 'offer_declined' ? 'mark as Offer Declined' : 'reject';
    const firstName = (candidate.first_name || '').trim();
    let preview;
    try {
      preview = await candidateAPI.previewOutcomeEmail(id, statusValue);
    } catch (error) {
      console.error('Error previewing outcome email:', error);
      const detail = error.response?.data?.detail || error.message;
      showAlert(
        pending
          ? `The ${label} could not be loaded (${detail}). Nothing has changed — ${firstName} is not ${statusValue === 'offer_declined' ? 'marked declined' : 'rejected'}. Try again, or set the status from Candidate Information and send the email from the grey bar.`
          : `The ${label} could not be loaded: ${detail}`,
        'error'
      );
      return;
    }
    const close = () => setEmailPreviewModal({ isOpen: false, emailData: null, emailType: null, onSendCallback: null });
    // Applies the pending decision (status + reason). The server archives on a
    // terminal status; the alert says so, as handleStatusUpdate's always has.
    const applyPending = async () => {
      if (!pending) return true;
      try {
        await candidateAPI.update(id, pending);
        return true;
      } catch (error) {
        console.error('Error updating status:', error);
        showAlert(`Failed to ${verb} ${firstName}: ${error.response?.data?.detail || error.message}`, 'error');
        return false;
      }
    };
    setEmailPreviewModal({
      isOpen: true,
      emailData: preview,
      emailType: 'outcome',
      title: pending ? `${statusValue === 'offer_declined' ? 'Mark' : 'Reject'} ${firstName}${statusValue === 'offer_declined' ? ' as Offer Declined' : ''} — email them?` : `Send the ${label}`,
      sendLabel: pending ? (statusValue === 'offer_declined' ? 'Send email & mark declined' : 'Send email & reject') : 'Send email',
      cancelLabel: pending ? 'Cancel — no change' : 'Cancel',
      note: 'This email goes from your own mailbox, not HR\'s — the reply comes back to you.',
      secondaryAction: pending ? {
        label: statusValue === 'offer_declined' ? 'Mark declined without emailing' : 'Reject without emailing',
        onClick: async () => {
          if (!await applyPending()) return;
          close();
          showAlert('Status updated. Candidate archived — still editable, and reachable from Archived. No email sent.', 'success');
          loadCandidate(true);
        },
      } : null,
      onSendCallback: async (editedSubject, editedBody) => {
        try {
          if (!await applyPending()) return;
          const data = await candidateAPI.sendOutcomeEmail(id, { subject: editedSubject, body: editedBody });
          close();
          if (data?.in_flight) {
            showAlert(`The ${label} is already being sent — wait for it to finish (or report a failure) before sending again.`, 'info');
            loadCandidate(true);
            return;
          }
          showAlert(`Sending ${label}…`, 'info');
          const outcome = await pollSendOutcome(data?.task_id);
          if (outcome === true) {
            showAlert(`✓ ${label.charAt(0).toUpperCase() + label.slice(1)} sent to ${candidate.email}`, 'success');
          } else if (outcome === false) {
            showAlert(`✗ The ${label} FAILED to send — nothing was delivered. Try again from the grey bar at the top.`, 'error');
          } else {
            showAlert(`The ${label} is still sending. Refresh in a moment — the grey bar at the top shows whether it went.`, 'info');
          }
          await loadCandidate(true);
        } catch (error) {
          console.error('Error sending outcome email:', error);
          showAlert(`Failed to send the ${label}: ${error.response?.data?.detail || error.message}`, 'error');
        }
      }
    });
  };

  const handleChecklistUpdate = async (field, value) => {
    try {
      // Update candidate via API and set the returned data (includes auto-updated status)
      const updated = await candidateAPI.update(id, { [field]: value });
      setCandidate(updated);
    } catch (error) {
      console.error('Error updating checklist:', error);
      // Revert on error
      loadCandidate(true);
      const errorMessage = error.response?.data?.detail || 'Failed to update field';
      showAlert(errorMessage, 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Interview pane. The rows follow each team's real flow (2026-10-03):
  //   reception   CV → social media check → phone interview → interview → 3h trial shift
  //   vets/nurses CV → background checks → interview (form filled during it) → trial day
  // The department comes from the position; office/cleaner follow the vets shape
  // but have no interview-form template (see INTERVIEW_FORM_TEMPLATES in config.py,
  // which is keyed per position so the pending student-nurse variant slots in).
  // ---------------------------------------------------------------------------
  const interviewDept = departmentForPosition(candidate?.position_applied);
  const isReception = interviewDept === 'reception';
  const hasInterviewForm = !!interviewDept && interviewDept !== 'office';
  const trialLabel = isReception ? 'Trial shift' : 'Trial day';
  const interviewSteps = isProgression
    ? { interview: 1 }
    : isReception
      ? { screening: 2, phone: 3, interview: 4, trial: 5 }
      : { screening: 2, interview: 3, trial: 4 };
  // Part 1 is complete when every circle is ✓ — five for reception (the phone
  // interview's "Invite to interview" included), four for everyone else. A ✗ is
  // an outcome, not completion. Like Parts 2–4 it then reads "Completed" and
  // starts folded; `checklist` stays undefined until someone clicks the header.
  const part1Complete = !!candidate && (isProgression
    ? candidate.in_person_interview_passed === true
    : (candidate.cv_received === true
      && candidate.background_check_passed === true
      && (!isReception || candidate.phone_interview_passed === true)
      && candidate.in_person_interview_passed === true
      && candidate.trial_day_passed === true));
  const part1Open = expandedSections.checklist !== undefined
    ? expandedSections.checklist
    : !part1Complete;
  useEffect(() => {
    if (part1Complete) {
      setExpandedSections(prev => ({ ...prev, checklist: undefined }));
    }
  }, [part1Complete]);
  const interviewFormUrl = candidate?.interview_form_doc_id
    ? `https://docs.google.com/document/d/${candidate.interview_form_doc_id}/edit`
    : null;
  // The job-description email is kept but demoted: collapsed unless someone opens
  // it, or one has actually been sent (so a real send is never hidden).
  const jdOpen = expandedSections.jobDescription !== undefined
    ? expandedSections.jobDescription
    : !!candidate?.job_description_sent_date;

  const cvInputRef = useRef(null);
  const coverLetterInputRef = useRef(null);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [creatingInterviewForm, setCreatingInterviewForm] = useState(false);

  const handleInterviewUpload = async (kind, event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // so choosing the same file again re-fires onChange
    if (!file) return;
    setUploadingKind(kind);
    try {
      const result = await candidateAPI.uploadDocument(id, kind, file);
      showAlert(`✓ ${result.message}${result.replaced ? ' — the previous version was replaced' : ''}`, 'success');
      loadCandidate(true);
    } catch (error) {
      console.error('Error uploading document:', error);
      showAlert(describeApiError(error, 'The file could not be uploaded'), 'error');
    } finally {
      setUploadingKind(null);
    }
  };

  const handleCreateInterviewForm = async (event) => {
    const replacing = !!candidate.interview_form_doc_id;
    if (replacing) {
      const ok = await showConfirm(
        'This candidate already has an interview form. Create a new, blank one? The existing form stays in their Drive folder.',
        event
      );
      if (!ok) return;
    }
    // Open the tab inside the click, then point it at the Doc once created —
    // a window.open after the await is treated as a pop-up and blocked.
    const tab = window.open('', '_blank');
    if (tab) tab.document.write('<p style="font-family: sans-serif; padding: 24px;">Creating the interview form…</p>');
    setCreatingInterviewForm(true);
    try {
      const result = await candidateAPI.createInterviewForm(id, replacing);
      if (tab) tab.location.href = result.url;
      loadCandidate(true);
    } catch (error) {
      if (tab) tab.close();
      console.error('Error creating interview form:', error);
      showAlert(describeApiError(error, 'The interview form could not be created'), 'error');
    } finally {
      setCreatingInterviewForm(false);
    }
  };

  // One indented line per file under step 1: status in the detail column, quiet
  // View · Replace / Upload links in the action column, so step 1 never wraps.
  // ---- Next step -----------------------------------------------------------
  // Which element pulses: the next thing HR should do, worked out from the
  // candidate's data rather than the status dropdown (a hand-set status must not
  // point at the wrong button). One per open part: before the contract is signed
  // only one part is live, after it Parts 5 and 6 run side by side. When the move
  // is the candidate's, a referee's or SignWell's, the matching status line glows
  // instead ("wait"), so the page still says what it is waiting for.
  const nextSteps = (() => {
    const glow = new Set();
    const wait = new Set();
    const c = candidate;
    if (!c || c.archived_date || TERMINAL_STATUSES.includes(c.status)) return { glow, wait };

    // Later evidence outranks earlier gaps: a candidate whose offer or documents
    // have gone out is past Part 1 whatever its tick boxes say (older records
    // have blanks there), and one whose contract has gone out is past Part 3.
    const beyondOffer = !!(c.offer_sent_date || c.documents_sent_date || c.contract_sent_date
      || c.contract_generated_date || c.candidate_signed_date);
    // Part 4 reads the same for everyone once the earlier parts are done.
    const part4 = () => {
      if (!c.start_date) return ['glow', 'p4:start_date'];
      if (!c.contract_sent_date) return ['glow', 'p4:send_contract'];
      // A signed contract never needs regenerating, whichever provider signed it
      // (contracts executed under the Google flow carry no SignWell id).
      if (c.contract_signed_date) return null;
      if (c.candidate_signed_date) return ['wait', 'p4:countersign'];
      if (!c.contract_generated_date || (usingSignWell && !c.contract_esign_document_id)) return ['glow', 'p4:generate'];
      return ['wait', 'p4:signature'];
    };
    const stage = (() => {
      if (isProgression) {
        // Interview → RCVS + qualification → contract. No offer, no pack.
        if (c.in_person_interview_passed === false) return null;
        if (c.in_person_interview_passed !== true && !c.contract_sent_date && !c.candidate_signed_date) {
          return ['glow', hasInterviewForm && !c.interview_form_doc_id ? 'p1:interview_form' : 'p1:interview'];
        }
        if (!areAllDocumentsReceived(c) && !c.contract_sent_date) {
          if (c.rcvs_number && !c.rcvs_verified) return ['glow', 'p3:rcvs'];
          return ['wait', 'p3:docs'];
        }
        return part4();
      }
      if (!beyondOffer) {
        // Part 1: the first unmet step AFTER the last one recorded. A failed
        // outcome ends the chain — the decision then is Reject, not a nudge.
        const steps = [
          ['p1:cv', !!(c.cv_received || c.cv_file_id), false],
          ['p1:screening', c.background_check_passed === true, false],
          ...(isReception ? [['p1:phone', c.phone_interview_passed === true, c.phone_interview_passed === false]] : []),
          [hasInterviewForm && !c.interview_form_doc_id ? 'p1:interview_form' : 'p1:interview',
            c.in_person_interview_passed === true, c.in_person_interview_passed === false],
          ['p1:trial', c.trial_day_passed === true, c.trial_day_passed === false],
        ];
        if (steps.some(([, , failed]) => failed)) return null;
        const lastDone = steps.map(([, done]) => done).lastIndexOf(true);
        const next = steps.slice(lastDone + 1).find(([, done]) => !done);
        return ['glow', next ? next[0] : 'p2:send_offer'];
      }
      if (!c.documents_sent_date && !c.contract_sent_date) return ['glow', 'p3:send_docs'];
      if (!areAllDocumentsReceived(c) && !c.contract_sent_date) return ['wait', 'p3:docs'];
      return part4();
    })();
    if (stage) (stage[0] === 'glow' ? glow : wait).add(stage[1]);

    // Acceptance: after the offer goes out, until the referee form, the documents
    // pack or HR's tick records it.
    if (beyondOffer && !c.offer_accepted_date && !c.candidate_signed_date) glow.add('p2:accept');

    // References run alongside Part 3 (the offer and the documents pack often go
    // out together), so they are a side item until one reference is in and
    // marked, or the candidate has signed.
    if (beyondOffer && requiresReferences && !c.candidate_signed_date) {
      const refs = c.references || [];
      const settled = refs.some(r => r.response_received_date && r.is_positive != null);
      if (!settled) {
        if (refs.length === 0) wait.add('p2:referees');
        else {
          const unsent = refs.find(r => !r.request_sent_date);
          const unmarked = refs.find(r => r.response_received_date && r.is_positive == null);
          if (unsent) glow.add(`p2:ref_send_${unsent.id}`);
          else if (unmarked) glow.add(`p2:ref_mark_${unmarked.id}`);
          else wait.add(`p2:ref_${refs[0].id}`);
        }
      }
    }

    // Part 3 side tasks: live whenever they apply, whatever the chain says.
    if (c.documents_sent_date || isProgression) {
      if (c.rcvs_number && !c.rcvs_verified) glow.add('p3:rcvs');
      if (c.medical_condition_flagged && !c.medical_interview_scheduled) glow.add('p3:medical');
      if (c.night_worker_form_received && !c.night_worker_assessment_completed) glow.add('p3:night');
    }

    // Parts 5 and 6 open together once the candidate has signed.
    let p5Done = false;
    let p6Done = false;
    if (c.candidate_signed_date) {
      if (onboardingData) {
        const next = onboardingData.tasks.find(t => !(onboardingData.checklist[t.key] || {}).completed);
        if (next) glow.add(`p5:${next.key}`); else p5Done = true;
      }
      if (inductionData?.visible) {
        const esigDocs = inductionData.documents.filter(d => d.requires_esignature);
        const unsigned = esigDocs.filter(d => !d.status.signed_date);
        const bundle = inductionData.esign_bundle;
        const failed = ['Declined', 'Canceled', 'Expired', 'Bounced', 'Error'].includes(bundle?.status);
        const isSent = d => (usingSignWell ? d.status.esign_document_id : d.status.sent_date);
        const allSent = unsigned.length > 0 && unsigned.every(isSent) && !failed;
        if (!inductionData.work_location) glow.add('p6:location');
        else if (!c.induction_email_sent_date) glow.add('p6:email');
        else if (unsigned.length > 0 && !allSent) glow.add('p6:esig');
        else if (unsigned.length > 0) wait.add('p6:esig');
        else if (pendingTrainingLog) {
          if (pendingTrainingLog.status?.signature_requested_date) wait.add('p6:training_log');
          else glow.add('p6:training_log');
        } else p6Done = true;
      }
    }
    if (c.contract_signed_date && p5Done && p6Done) glow.add('top:recruited');
    return { glow, wait };
  })();
  const isNext = (key) => nextSteps.glow.has(key);
  const isWaiting = (key) => nextSteps.wait.has(key);
  const glowIf = (key) => (isNext(key) ? ' next-glow' : '');
  const partHasNext = (part) => [...nextSteps.glow, ...nextSteps.wait].some(k => k.startsWith(`${part}:`));

  const renderFileRow = (kind, label, fileId, inputRef) => (
    <div className="interview-row interview-file-row">
      <div className="interview-step-mark" />
      <div className="interview-step-label">{label}</div>
      <div className="interview-step-detail">
        <span>{fileId ? '✓ in the candidate\'s Documents folder' : 'not uploaded'}</span>
      </div>
      <div className="interview-step-actions">
        <span className="interview-link-pair">
          {fileId ? (
            <>
              <button
                className="btn-link-small btn-link-view"
                onClick={() => openCandidateDocument(kind, `https://drive.google.com/file/d/${fileId}/view`)}
              >
                View
              </button>
              <span className="interview-link-dot">·</span>
              <button
                className="btn-link-small btn-link-replace"
                onClick={() => inputRef.current?.click()}
                disabled={uploadingKind !== null}
              >
                {uploadingKind === kind ? 'Uploading…' : 'Replace'}
              </button>
            </>
          ) : (
            <>
              <button
                className={`btn-link-small btn-link-view${kind === 'cv' && isNext('p1:cv') ? ' next-glow-text' : ''}`}
                onClick={() => inputRef.current?.click()}
                disabled={uploadingKind !== null}
              >
                {uploadingKind === kind ? 'Uploading…' : 'Upload'}
              </button>
              <span />
              <span />
            </>
          )}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.png,.jpg,.jpeg"
          style={{ display: 'none' }}
          onChange={(e) => handleInterviewUpload(kind, e)}
        />
      </div>
    </div>
  );

  // Per-row "Book on calendar" button (optional — many teams arrange times by
  // phone). Once booked it reads "Booked ✓" and the date shows in the detail
  // column; clicking again books another time.
  const renderBookLink = (type, bookedDate, what) => (
    <button
      className="btn-link-small btn-link-view interview-sub-link"
      onClick={() => handleOpenScheduleModal(type)}
      title={bookedDate ? `Booked for ${formatDateTime(bookedDate)} — click to book another time` : `Book the ${what} in the calendar and invite the candidate`}
    >
      {bookedDate ? 'Booked ✓' : 'Book on calendar'}
    </button>
  );

  // The tick-box rows use the same circle as the outcome rows, so the left column
  // is one line of marks. Clicking the circle (or the label) toggles the field.
  const toggleOfferAccepted = () => handleChecklistUpdate(
    'offer_accepted_date', candidate.offer_accepted_date ? null : new Date().toISOString()
  );
  const renderToggleMark = (checked, onToggle, ariaLabel, glowKey) => (
    <div className="interview-step-mark">
      <span
        role="checkbox"
        aria-checked={checked}
        aria-label={ariaLabel}
        tabIndex={0}
        className={`interview-outcome-mark clickable ${checked ? 'passed' : ''}${glowIf(glowKey)}`}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle(); } }}
      >
        {checked ? '✓' : ''}
      </span>
    </div>
  );

  // The left-column mark for an outcome row: an empty circle until recorded, then a
  // green tick or coral cross — so the column reads as one line of status marks
  // alongside the tick boxes above it.
  // Clicking cycles pending → passed → not passed → pending (there is no dropdown
  // any more); the words are written in the row's detail column so the state is
  // readable, not just a colour. Passing advances the status exactly as before.
  const nextOutcome = (passed) => (passed === true ? false : passed === false ? null : true);
  const outcomeWords = (passed, yes = 'Passed', no = 'Not passed') =>
    (passed === true ? yes : passed === false ? no : null);
  const renderOutcomeMark = (passed, field, ariaLabel, yes = 'Passed', no = 'Not passed') => (
    <div className="interview-step-mark">
      <span
        role="button"
        tabIndex={0}
        aria-label={`${ariaLabel}: ${outcomeWords(passed, yes, no) || 'pending'} — click to change`}
        title={passed === true ? `${yes} — click for ${no}` : passed === false ? `${no} — click to clear` : `Click to mark ${yes}`}
        className={`interview-outcome-mark clickable ${passed === true ? 'passed' : passed === false ? 'failed' : ''}${glowIf({ phone_interview_passed: 'p1:phone', in_person_interview_passed: 'p1:interview', trial_day_passed: 'p1:trial' }[field])}`}
        onClick={() => handleChecklistUpdate(field, nextOutcome(passed))}
        onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleChecklistUpdate(field, nextOutcome(passed)); } }}
      >
        {passed === true ? '✓' : passed === false ? '✗' : ''}
      </span>
    </div>
  );

  // "Import from email": pick a CV / cover letter out of a recent email in the
  // clicking user's own inbox or HR's, and file it exactly as an upload would.
  const [emailAttach, setEmailAttach] = useState({
    open: false, query: '', loading: false, results: [], inboxes: [], errors: [], attaching: null,
  });

  const runEmailAttachSearch = async (query) => {
    setEmailAttach(prev => ({ ...prev, loading: true, errors: [] }));
    try {
      const data = await candidateAPI.listEmailAttachments(id, query);
      setEmailAttach(prev => ({
        ...prev, loading: false,
        results: data.attachments || [], inboxes: data.inboxes || [], errors: data.inbox_errors || [],
      }));
    } catch (error) {
      console.error('Error searching email attachments:', error);
      setEmailAttach(prev => ({
        ...prev, loading: false, results: [],
        errors: [describeApiError(error, 'The search failed. Please try again.')],
      }));
    }
  };

  const openEmailAttachModal = () => {
    const query = candidate.email || '';
    setEmailAttach({ open: true, query, loading: true, results: [], inboxes: [], errors: [], attaching: null });
    runEmailAttachSearch(query);
  };

  const closeEmailAttachModal = () => setEmailAttach(prev => ({ ...prev, open: false }));

  const handleAttachFromEmail = async (item, kind) => {
    const key = `${item.inbox}:${item.message_id}:${item.filename}:${kind}`;
    setEmailAttach(prev => ({ ...prev, attaching: key }));
    try {
      const result = await candidateAPI.attachFromEmail(id, {
        inbox: item.inbox, message_id: item.message_id, filename: item.filename, kind,
      });
      setEmailAttach(prev => ({ ...prev, open: false, attaching: null }));
      showAlert(`✓ ${result.message} from email${result.replaced ? ' — the previous version was replaced' : ''}`, 'success');
      loadCandidate(true);
    } catch (error) {
      console.error('Error attaching from email:', error);
      setEmailAttach(prev => ({ ...prev, attaching: null }));
      showAlert(describeApiError(error, 'The attachment could not be filed'), 'error');
    }
  };

  const handleStartDateChange = (e) => {
    // Update the input field immediately for user feedback
    setCandidate(prev => ({ ...prev, start_date: e.target.value }));
  };

  const handleStartDateBlur = (e) => {
    const newDate = e.target.value;

    // If the date hasn't changed from original or is empty, do nothing
    if (!newDate || newDate === originalStartDate) {
      // Revert to original if empty
      if (!newDate) {
        setCandidate(prev => ({ ...prev, start_date: originalStartDate }));
      }
      return;
    }

    // For TEMPORARY positions, wait until end date is entered (modal triggered on end date)
    // For PERMANENT positions, show modal on start date entry
    if (!candidate.claire_notified_date && newDate && newDate.length === 10 && candidate.employment_type !== 'TEMPORARY') {
      setPendingStartDate(newDate);
      setShowClaireConfirmModal(true);
    } else {
      // If Claire was already notified or it's a temporary position, update directly
      handleChecklistUpdate('start_date', newDate);
    }
  };

  const handleConfirmClaireNotification = async () => {
    try {
      // For TEMPORARY positions, also save the end_date if it exists
      const updateData = { start_date: pendingStartDate };
      if (candidate.employment_type === 'TEMPORARY' && candidate.end_date) {
        updateData.end_date = candidate.end_date;
      }

      // Update the start date (and end date for temporary)
      await candidateAPI.update(id, updateData);

      // Then notify Claire
      await candidateAPI.notifyClaire(id);

      // Reload candidate data to reflect changes
      await loadCandidate(true);

      setShowClaireConfirmModal(false);
      setPendingStartDate(null);
      showAlert('Start date set and Claire has been notified!', 'success');
    } catch (error) {
      console.error('Error setting start date and notifying Claire:', error);
      showAlert('Failed to set start date or notify Claire', 'error');
      // Revert to original on error
      setCandidate(prev => ({ ...prev, start_date: originalStartDate }));
    }
  };

  // × or a click outside the box saves nothing. Reload so the field shows what
  // the database holds rather than the date just typed — it used to stay in the
  // box looking saved until the next page load (Patrick Coyne, 2026-10-07).
  const handleDismissClaireModal = () => {
    setShowClaireConfirmModal(false);
    setPendingStartDate(null);
    loadCandidate(true);
  };

  const handleCancelClaireNotification = async () => {
    try {
      // For TEMPORARY positions, also save the end_date if it exists
      const updateData = { start_date: pendingStartDate };
      if (candidate.employment_type === 'TEMPORARY' && candidate.end_date) {
        updateData.end_date = candidate.end_date;
      }

      // Update the start date (and end date for temporary) without notifying Claire
      await candidateAPI.update(id, updateData);

      // Update the original start date so subsequent changes are compared correctly
      setOriginalStartDate(pendingStartDate);

      await loadCandidate(true);

      setShowClaireConfirmModal(false);
      setPendingStartDate(null);
    } catch (error) {
      console.error('Error setting start date:', error);
      showAlert('Failed to set start date', 'error');
      // Revert to original on error
      setCandidate(prev => ({ ...prev, start_date: originalStartDate }));
    }
  };

  const handleGenerateContract = async (event) => {
    if (generatingContract) return; // Prevent concurrent requests
    if (!candidate.start_date) {
      showAlert('Please set a start date before generating contract', 'info');
      return;
    }

    // Under SignWell the contract is sent the moment it is generated — there is
    // no Doc to read first. So show the one part of it that varies per person,
    // the Hours of work block, exactly as it will render, before asking to go
    // ahead. If that wording disagrees with Hours per Week the backend would
    // refuse anyway; say so now instead.
    let hoursPreview = null;
    try {
      hoursPreview = await candidateAPI.contractHoursOfWork(id);
    } catch (err) {
      hoursPreview = null; // older backend or transient failure: confirm without it
    }
    if (hoursPreview?.warning) {
      showAlert(hoursPreview.warning, 'error');
      return;
    }

    const lines = [];
    // Regenerating replaces the contract of record: the SignWell request already
    // sent is cancelled (its link stops working) and a new one goes out, and any
    // signature collected on the old version no longer counts. Say so first.
    if (candidate.contract_generated_date) {
      lines.push('This will generate a NEW contract from the current candidate details.');
      if (candidate.contract_esign_document_id) {
        lines.push('The SignWell request already sent will be cancelled — its link will stop working — and a new request will be emailed.');
      }
      if (candidate.contract_signed_date) {
        lines.push('The existing signed contract will no longer count; both parties must sign again.');
      } else if (candidate.candidate_signed_date) {
        lines.push('The candidate\'s existing signature will no longer count; they must sign again.');
      }
    }
    if (hoursPreview?.text) {
      lines.push(
        `Hours of work, as it will appear in the contract (${hoursPreview.is_standard ? 'standard wording for this position' : "this candidate's own wording"}):\n\n${hoursPreview.text}`
      );
    }
    lines.push(
      candidate.contract_generated_date
        ? 'Claire Hopkins will be re-notified. Continue?'
        : 'Generate the contract and send it to the candidate for signature?'
    );
    if (!await showConfirm(lines.join('\n\n'), event)) return;

    setGeneratingContract(true);
    try {
      const response = await fetch(`${API_URL}/api/candidates/${id}/generate-contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate contract');
      }

      const result = await response.json();

      // Reload candidate to get updated contract_generated_date
      await loadCandidate(true);

      if (result.esign_provider === 'signwell') {
        // The request has already gone out through SignWell — there is no Doc
        // to open for a manual eSignature step. In test mode nothing was
        // emailed, so keep the signing links for the panel in Step 2.
        setSignwellSigningUrls(result.signing_urls || null);
        showAlert(
          result.esign_test_mode
            ? 'Contract generated and a SignWell request created in TEST MODE — no email was sent. Use the signing links in Step 2.\n\nClaire Hopkins has been notified via email.'
            : 'Contract generated and sent to the candidate for signature via SignWell.\n\nClaire Hopkins has been notified via email.',
          'success'
        );
      } else {
        // Open contract in new tab
        window.open(result.contract_url, '_blank');
        showAlert('Contract generated successfully! Opening in new tab...\n\nClaire Hopkins has been notified via email.', 'success');
      }
    } catch (error) {
      console.error('Error generating contract:', error);
      showAlert(`Failed to generate contract: ${error.message}`, 'error');
    } finally {
      setGeneratingContract(false);
    }
  };

  const handleSendContractEmail = async (event) => {
    if (!await showConfirm('Send contract email to candidate?', event)) return;

    try {
      // Get email preview from backend
      const previewResponse = await candidateAPI.previewContractEmail(id);

      // Show preview modal
      setEmailPreviewModal({
        isOpen: true,
        emailData: previewResponse,
        emailType: 'contract',
        onSendCallback: async (editedSubject, editedBody) => {
          try {
            // Send with custom content
            const response = await fetch(`${API_URL}/api/candidates/${id}/send-contract-email`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({
                custom_content: {
                  subject: editedSubject,
                  body: editedBody
                }
              })
            });

            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.detail || 'Failed to send contract email');
            }

            const data = await response.json();
            setEmailPreviewModal({ isOpen: false, emailData: null, emailType: null, onSendCallback: null });
            if (data?.in_flight) {
              showAlert('A contract email is already being sent to this candidate — wait for it to finish (or report a failure) before sending again.', 'info');
              loadCandidate(true);
              return;
            }
            showAlert('Sending contract email…', 'info');

            // Poll for the REAL outcome — status/sent_date are set in the task
            // only on a confirmed send, so a failure must surface here.
            const outcome = await pollSendOutcome(data?.task_id);
            if (outcome === true) {
              showAlert(`✓ Contract email sent to ${candidate.email}`, 'success');
            } else if (outcome === false) {
              showAlert('✗ Contract email FAILED to send — nothing was delivered to the candidate. Please try again.', 'error');
            } else {
              showAlert('Contract email is still sending. Refresh in a moment — if the button has not turned green, it did not send.', 'info');
            }
            await loadCandidate(true);
          } catch (error) {
            console.error('Error sending contract email:', error);
            showAlert(`Failed to send contract email: ${error.message}`, 'error');
          }
        }
      });
    } catch (error) {
      console.error('Error previewing contract email:', error);
      showAlert(`Failed to preview contract email: ${error.response?.data?.detail || error.message}`, 'error');
    }
  };

  // Manual fallback when automatic eSignature detection misses the candidate's signing.
  const handleMarkCandidateSigned = async (event) => {
    if (!await showConfirm('Mark the candidate as having signed?\n\nThis unlocks Part 5 onboarding and sends the staff onboarding notifications, exactly as automatic detection would. The employer countersignature can still be recorded later.', event)) return;

    try {
      const response = await fetch(`${API_URL}/api/candidates/${id}/mark-candidate-signed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to mark candidate as signed');
      }

      await loadCandidate(true);
      showAlert('Candidate marked as signed. Onboarding (Part 5) is now unlocked.', 'success');
    } catch (error) {
      console.error('Error marking candidate as signed:', error);
      showAlert(`Failed to mark candidate as signed: ${error.message}`, 'error');
    }
  };

  const handleMarkFullyExecuted = async (event) => {
    if (!await showConfirm('Mark the contract as fully executed (all parties have signed)?', event)) return;

    try {
      const response = await fetch(`${API_URL}/api/candidates/${id}/mark-contract-signed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to mark contract as fully executed');
      }

      await loadCandidate(true);
      showAlert('Contract marked as fully executed.', 'success');
    } catch (error) {
      console.error('Error marking contract as fully executed:', error);
      showAlert(`Failed to mark contract as fully executed: ${error.message}`, 'error');
    }
  };

  // Removed handleNotifyClaire - Claire is now automatically notified when contract is generated

  // PART 5: Pre-start onboarding checklist functions
  const loadOnboardingData = useCallback(async () => {
    if (!candidate?.candidate_signed_date) return; // Only load once the candidate has signed

    try {
      setOnboardingLoading(true);
      const response = await fetch(`${API_URL}/api/candidates/${id}/onboarding`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOnboardingData(data);
      }
    } catch (error) {
      console.error('Error loading onboarding data:', error);
    } finally {
      setOnboardingLoading(false);
    }
  }, [id, candidate?.candidate_signed_date]);

  useEffect(() => {
    if (candidate) {
      loadOnboardingData();
    }
  }, [candidate, loadOnboardingData]);

  const handleToggleAllTasks = async () => {
    if (!onboardingData) return;

    const allCompleted = onboardingData.stats.completion_percentage === 100;
    const newState = !allCompleted;

    // Update all tasks
    for (const task of onboardingData.tasks) {
      await handleOnboardingTaskUpdate(task.key, newState, '');
    }
  };

  const handleOnboardingTaskUpdate = async (taskKey, completed, notes = '') => {
    try {
      const response = await fetch(`${API_URL}/api/candidates/${id}/onboarding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          task_key: taskKey,
          completed: completed,
          completed_by: 'HR Team', // Could be made dynamic if you have user name
          notes: notes
        })
      });

      if (response.ok) {
        const result = await response.json();
        setOnboardingData(prev => ({
          ...prev,
          checklist: result.checklist,
          stats: result.stats
        }));
      } else {
        throw new Error('Failed to update task');
      }
    } catch (error) {
      console.error('Error updating onboarding task:', error);
      showAlert('Failed to update task', 'error');
    }
  };

  // PART 6: Induction Folder functions
  const loadInductionData = useCallback(async () => {
    if (!candidate?.candidate_signed_date) return; // Only load once the candidate has signed

    try {
      setInductionLoading(true);
      const data = await candidateAPI.getInduction(id);
      setInductionData(data);
    } catch (error) {
      console.error('Error loading induction data:', error);
    } finally {
      setInductionLoading(false);
    }
  }, [id, candidate?.candidate_signed_date]);

  useEffect(() => {
    if (candidate) {
      loadInductionData();
    }
  }, [candidate, loadInductionData]);

  const handleGenerateSingleInductionDoc = async (docKey) => {
    // Prevent double-clicking the same button
    if (inductionGeneratingDocs[docKey]) {
      return;
    }

    try {
      setInductionGeneratingDocs(prev => ({ ...prev, [docKey]: true }));
      const result = await candidateAPI.generateInductionDocument(id, docKey);

      if (result.success) {
        // Auto-open document in new tab for eSignature request
        if (result.pdf_url) {
          window.open(result.pdf_url, '_blank');
        }

        // Reload induction data to get updated status
        await loadInductionData();
      }
    } catch (error) {
      console.error('Error generating induction document:', error);
      showAlert(`Failed to generate document: ${error.response?.data?.detail || error.message}`, 'error');
    } finally {
      setInductionGeneratingDocs(prev => ({ ...prev, [docKey]: false }));
    }
  };

  // Reads back from Google Drive whether the manual eSignature requests were
  // actually launched, and who they went to. Generating a document tells us
  // nothing about that - the request is raised by hand in the Drive UI, and on
  // 18/08/2026 that step was missed for two candidates while the app showed the
  // documents as sent.
  const handleVerifyInductionRequests = async () => {
    if (verifyingInductionRequests) return;

    try {
      setVerifyingInductionRequests(true);
      const result = await candidateAPI.verifyInductionRequests(id);
      await loadInductionData();

      const sent = result.sent || [];
      const misdirected = result.misdirected || [];
      const notLaunched = result.not_launched || [];
      const unknown = result.unknown || [];

      if (misdirected.length > 0) {
        showAlert(
          `⚠ ${misdirected.length} eSignature request(s) are not addressed to ${candidate.email}.\n\n` +
          'The request exists in Drive but the candidate is not one of its signers — ' +
          'it may have been sent to you instead, or to a mistyped address. ' +
          'Check the recipient shown on each document and re-send if needed.',
          'error'
        );
      } else if (notLaunched.length > 0) {
        showAlert(
          `${notLaunched.length} document(s) have been generated but no eSignature request has been sent yet.\n\n` +
          'Open each one with View and request the eSignature in Google Docs, then check again.',
          'warning'
        );
      } else if (unknown.length > 0) {
        showAlert('Could not reach Google Drive to check the requests. Please try again shortly.', 'error');
      } else if (sent.length > 0) {
        showAlert(`✓ All ${sent.length} eSignature request(s) confirmed sent to ${candidate.email}.`, 'success');
      } else {
        showAlert('Nothing to check — no unsigned eSignature documents have been generated yet.', 'info');
      }
    } catch (error) {
      console.error('Error verifying eSignature requests:', error);
      showAlert(`Failed to check eSignature requests: ${error.response?.data?.detail || error.message}`, 'error');
    } finally {
      setVerifyingInductionRequests(false);
    }
  };

  // SignWell: one click generates whatever is missing and sends every unsigned
  // eSignature document as ONE SignWell request — no Doc tabs, no manual step.
  const [sendingInductionEsign, setSendingInductionEsign] = useState(false);
  const handleSendInductionEsignatures = async (event) => {
    if (!inductionData?.documents || sendingInductionEsign) return;
    const unsigned = inductionData.documents.filter(doc => doc.requires_esignature && !doc.status.signed_date);
    if (unsigned.length === 0) {
      showAlert('All eSignature documents are already signed', 'info');
      return;
    }
    const confirmed = await showConfirm(
      `Send ${unsigned.length} induction document${unsigned.length === 1 ? '' : 's'} to ${candidate.email} for signature via SignWell?\n\n` +
      unsigned.map(doc => `• ${doc.label}`).join('\n') +
      '\n\nThe candidate receives ONE email and signs them all in one go.',
      event
    );
    if (!confirmed) return;

    try {
      setSendingInductionEsign(true);
      const result = await candidateAPI.sendInductionEsignatureRequest(id);
      await loadInductionData();
      await loadCandidate(true);
      if (result.in_flight) {
        showAlert(result.message, 'info');
      } else if (result.esign_test_mode) {
        showAlert(
          `SignWell request created in TEST MODE for ${result.sent.length} document(s) — no email was sent. ` +
          'Use the signing link shown under the button.',
          'success'
        );
      } else {
        showAlert(`✓ ${result.sent.length} induction document(s) sent to ${candidate.email} for signature via SignWell.`, 'success');
      }
    } catch (error) {
      console.error('Error sending induction documents via SignWell:', error);
      showAlert(`Failed to send induction documents: ${error.response?.data?.detail || error.message}`, 'error');
    } finally {
      setSendingInductionEsign(false);
    }
  };

  // SignWell fallback for "I never got the email": fetch the open request's
  // links and copy the one for whoever still has to sign (candidate first, then
  // the employer signer) so HR can paste it into an ordinary email or text.
  const [copyingEsignLink, setCopyingEsignLink] = useState(null);
  const handleCopyEsignLink = async (kind) => {
    if (copyingEsignLink) return;
    try {
      setCopyingEsignLink(kind);
      const result = await candidateAPI.getEsignSigningLinks(id, kind);
      const pending = (result.links || []).find(l => l.signing_url && l.status !== 'completed' && l.status !== 'signed')
        || (result.links || []).find(l => l.signing_url);
      if (!pending) {
        showAlert('SignWell returned no signing link for this request.', 'error');
        return;
      }
      try {
        await navigator.clipboard.writeText(pending.signing_url);
        showAlert(
          `Signing link for ${pending.name || pending.recipient} (${pending.email}) copied to the clipboard.\n\n` +
          'Paste it into an email or text to them — it is the same link SignWell emailed' +
          (result.test_mode ? ' (test mode: no email was sent).' : '.'),
          'info'  // not 'success': success alerts auto-close after 3 s, too fast to read this
        );
      } catch (clipErr) {
        // Clipboard can be refused (non-secure context, permissions) — show it instead.
        window.prompt(`Signing link for ${pending.name || pending.recipient} — copy it:`, pending.signing_url);
      }
    } catch (error) {
      showAlert(`Could not fetch the signing link: ${error.response?.data?.detail || error.message}`, 'error');
    } finally {
      setCopyingEsignLink(null);
    }
  };

  const handleGenerateAllInductionDocs = async (event) => {
    if (!inductionData?.documents) return;

    // Only generate eSignature-required documents
    const docsToGenerate = inductionData.documents.filter(doc =>
      doc.template_id && !doc.status.generated_date && doc.requires_esignature
    );

    if (docsToGenerate.length === 0) {
      showAlert('All eSignature documents have already been generated', 'info');
      return;
    }

    const confirmed = await showConfirm(
      `Generate ${docsToGenerate.length} eSignature documents?\n\n` +
      docsToGenerate.map(doc => `• ${doc.label}`).join('\n') +
      '\n\nEach document will open in a new tab for eSignature request.',
      event
    );

    if (!confirmed) return;

    try {
      // Generate documents sequentially with minimal delay to avoid SSL errors
      console.log(`Generating ${docsToGenerate.length} documents sequentially...`);

      for (const doc of docsToGenerate) {
        await handleGenerateSingleInductionDoc(doc.key);

        // Minimal 100ms delay between documents to avoid SSL issues
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      showAlert(
        `✓ All ${docsToGenerate.length} documents generated — they have NOT been sent yet.\n\n` +
        'Request the eSignature on each one in the opened tabs, then press ' +
        '"Check requests sent" to confirm they reached the candidate.',
        'success'
      );

      // Reload induction data to reflect updates
      await loadInductionData();
    } catch (error) {
      console.error('Error generating all documents:', error);
      showAlert('Error generating documents. Please try again or generate individually.', 'error');
    }
  };

  const handleSendInductionEmail = async (event) => {
    if (!inductionData?.documents) return;

    // Only non-eSignature documents will be attached to the email
    const attachmentDocs = inductionData.documents.filter(doc =>
      doc.status.generated_date && !doc.requires_esignature && doc.kind !== 'training_log'
    );

    if (attachmentDocs.length === 0) {
      showAlert('No documents ready to send. Generate documents first.', 'info');
      return;
    }

    const confirmed = await showConfirm(
      `Send induction email with ${attachmentDocs.length} attached documents to employee?\n\n` +
      `Recipient: ${candidate.email}\n\n` +
      `Attachments:\n` +
      attachmentDocs.map(doc => `• ${doc.label}`).join('\n') +
      `\n\nNote: eSignature documents will be sent separately by Google.`,
      event
    );

    if (!confirmed) return;

    try {
      // Get email preview from backend
      const previewResponse = await candidateAPI.previewInductionEmail(id);

      // Show preview modal
      setEmailPreviewModal({
        isOpen: true,
        emailData: previewResponse,
        emailType: 'induction',
        onSendCallback: async (editedSubject, editedBody) => {
          try {
            // Send with custom content
            const resp = await candidateAPI.sendInductionEmail(id, {
              subject: editedSubject,
              body: editedBody
            });
            setEmailPreviewModal({ isOpen: false, emailData: null, emailType: null, onSendCallback: null });
            if (resp?.in_flight) {
              showAlert('An induction email is already being sent to this candidate — wait for it to finish (or report a failure) before sending again.', 'info');
              loadCandidate(true);
              return;
            }
            showAlert('Sending induction documents email…', 'info');

            // Poll for the REAL outcome — per-doc sent_date + status are stamped
            // in the task only on a confirmed send, so a failure must surface here.
            const outcome = await pollSendOutcome(resp?.task_id);
            await loadInductionData();
            await loadCandidate(true); // refresh candidate so the send-status subtitle updates
            if (outcome === true) {
              showAlert(`✓ Induction documents email sent to ${candidate.email}`, 'success');
            } else if (outcome === false) {
              showAlert('✗ Induction email FAILED to send — nothing was delivered to the candidate. Please try again.', 'error');
            } else {
              showAlert('Induction email is still sending. Refresh in a moment — if the button has not turned green, it did not send.', 'info');
            }
          } catch (error) {
            console.error('Error sending induction email:', error);
            showAlert(`Failed to send email: ${error.response?.data?.detail || error.message}`, 'error');
          }
        }
      });
    } catch (error) {
      console.error('Error previewing induction email:', error);
      showAlert(`Failed to preview induction email: ${error.response?.data?.detail || error.message}`, 'error');
    }
  };

  const handleVerifyRcvs = async () => {
    if (!candidate.rcvs_number) {
      showAlert('No RCVS number to verify', 'info');
      return;
    }

    setRcvsVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/candidates/${id}/verify-rcvs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Verification failed');
      }

      if (result.verified) {
        // Save verification status to database
        const verifiedDate = new Date().toISOString();
        await candidateAPI.update(id, {
          rcvs_verified: true,
          rcvs_verified_date: verifiedDate
        });

        // Update local state
        setCandidate(prev => ({ ...prev, rcvs_verified: true, rcvs_verified_date: verifiedDate }));
        setRcvsVerificationFailed(false);
        showAlert(`✓ RCVS Number Verified!\n\n${result.details || 'Registration found on RCVS website'}\n${result.name_match ? '\nName matches records.' : '\nNote: Please manually verify the name matches.'}`, 'success');
      } else {
        setRcvsVerificationFailed(true);
        showAlert(`✗ Verification Failed\n\n${result.error || result.details || 'No registration found with this number'}`, 'error');
      }
    } catch (error) {
      console.error('Error verifying RCVS:', error);
      setRcvsVerificationFailed(true);
      showAlert(`Error verifying RCVS number: ${error.message}`, 'error');
    } finally {
      setRcvsVerifying(false);
    }
  };

  // "Progress to RVN": a new linked record for the RVN position (see the
  // isProgression comment above). This record is left exactly as it is.
  const handleProgress = async (event) => {
    const confirmed = await showConfirm(
      `Progress ${candidate.first_name} to RVN?\n\n` +
      'This creates a new candidate record for the RVN position, linked to this one: ' +
      'an interview with the SVN/RVN form, the RCVS registration and proof of qualification, ' +
      'a new contract, then name badge, uniform, door plate and VDS, and the dispensary training log. ' +
      'This record stays as it is.',
      event
    );
    if (!confirmed) return;
    setProgressing(true);
    try {
      const created = await candidateAPI.progress(id, 'rvn');
      showAlert(`New RVN record created for ${created.first_name} ${created.last_name}. Opening it now.`, 'success');
      navigate(`/candidates/${created.id}`);
    } catch (error) {
      console.error('Error creating progression:', error);
      showAlert(`Could not create the progression: ${error.response?.data?.detail || error.message}`, 'error');
    } finally {
      setProgressing(false);
    }
  };

  const handleOpenScheduleModal = (type) => {
    setScheduleType(type);
    setShowScheduleModal(true);
    // Set default duration based on type. Reception runs a 2–3 hour trial
    // shift; vets and nurses a full trial day. Editable in the modal.
    if (type === 'trial-day') {
      setScheduleDuration(isReception ? 180 : 480);
    } else if (type === 'phone-interview') {
      setScheduleDuration(30); // 30 minutes
    } else {
      setScheduleDuration(60); // 1 hour (in-person interview)
    }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();

    if (!scheduleDateTime) {
      showAlert('Please select a date and time', 'info');
      return;
    }

    const endpoints = {
      'phone-interview': `/api/candidates/${id}/schedule-phone-interview`,
      'in-person-interview': `/api/candidates/${id}/schedule-in-person-interview`,
      'trial-day': `/api/candidates/${id}/schedule-trial-day`
    };

    try {
      // Convert datetime-local format to ISO format
      const isoDateTime = new Date(scheduleDateTime).toISOString();

      const response = await candidateAPI.api.post(endpoints[scheduleType], {
        interview_datetime: isoDateTime,
        duration_minutes: parseInt(scheduleDuration)
      });

      showAlert(response.data.message + (response.data.event_link ? `\nCalendar event: ${response.data.event_link}` : ''), 'success');
      setShowScheduleModal(false);
      setScheduleDateTime('');
      loadCandidate(true);
    } catch (error) {
      console.error('Error scheduling:', error);
      showAlert('Failed to schedule: ' + (error.response?.data?.detail || 'Unknown error'), 'error');
    }
  };

  // FastAPI rejects a bad field with 422 and detail: [{loc:[...,'annual_salary'],
  // msg:'...'}]. Surfacing that matters — a salary typed as "£23,793" used to show
  // only "Failed to preview offer email", so there was no way to tell which box was
  // wrong (candidate 21: three attempts, then the offer was abandoned).
  const describeApiError = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail
        .map((d) => {
          const rawField = String(d.loc?.[d.loc.length - 1] || '');
          const field = capitalizeWords(rawField.replace(/_/g, ' '));
          const msg = String(d.msg || '').replace(/^Value error,\s*/, '');
          return field ? `${field} — ${msg}` : msg;
        })
        .join('\n');
    }
    return fallback;
  };

  const handleOpenJobOfferModal = () => {
    // Open with whatever is already on the record — usually entered in the
    // Candidate Information pane — so nobody has to retype terms that are already
    // known. Retyping was where stray currency symbols and pence crept in, and the
    // old hardcoded defaults were a live hazard: open, send, and you would have
    // offered 40 hours at £45,000 regardless of what was agreed.
    const hasAgreedTerms =
      candidate.hours_per_week != null ||
      candidate.hours_per_shift != null ||
      candidate.annual_salary != null ||
      candidate.hourly_salary != null;

    if (hasAgreedTerms) {
      const hoursPerWeek = candidate.hours_per_week ?? '';
      const annualSalary = candidate.annual_salary ?? '';
      setJobOfferForm({
        hours_per_week: hoursPerWeek,
        hours_per_shift: candidate.hours_per_shift ?? '',
        annual_salary: annualSalary,
        // Fall back to the same calculation the form uses when the record has the
        // salary and hours but no stored hourly rate.
        hourly_salary: candidate.hourly_salary || calculateHourlyRate(annualSalary, hoursPerWeek),
        // The record holds wording only when it was edited away from the standard
        // text; otherwise show the standard text for these hours.
        hours_of_work: candidate.hours_of_work || standardHoursOfWork(candidate.position_applied, hoursPerWeek),
        hours_of_work_edited: !!candidate.hours_of_work,
      });
    } else {
      setJobOfferForm({
        hours_per_week: '40',
        hours_per_shift: '8',
        annual_salary: '45000',
        hourly_salary: '21.63',
        hours_of_work: standardHoursOfWork(candidate.position_applied, '40'),
        hours_of_work_edited: false,
      });
    }
    setShowJobOfferModal(true);
  };

  const calculateHourlyRate = (annualSalary, hoursPerWeek) => {
    if (annualSalary && hoursPerWeek && hoursPerWeek > 0) {
      const hourlyRate = annualSalary / (hoursPerWeek * 52);
      return hourlyRate.toFixed(2);
    }
    return '';
  };

  const handleJobOfferFormChange = (field, value) => {
    const updatedForm = { ...jobOfferForm, [field]: value };

    // Auto-calculate hourly rate when annual salary or hours per week change
    if (field === 'annual_salary' || field === 'hours_per_week') {
      updatedForm.hourly_salary = calculateHourlyRate(
        updatedForm.annual_salary,
        updatedForm.hours_per_week
      );
    }

    // The Hours of work box re-fills with the standard wording as the hours
    // change — but only while it is untouched. An edited paragraph is never
    // silently overwritten; "Reset to standard" is the way back.
    if (field === 'hours_per_week' && !updatedForm.hours_of_work_edited) {
      updatedForm.hours_of_work = standardHoursOfWork(candidate.position_applied, value);
    }
    if (field === 'hours_of_work') {
      updatedForm.hours_of_work_edited =
        value !== standardHoursOfWork(candidate.position_applied, updatedForm.hours_per_week);
    }

    setJobOfferForm(updatedForm);
  };

  const resetJobOfferHoursOfWork = () => {
    setJobOfferForm(prev => ({
      ...prev,
      hours_of_work: standardHoursOfWork(candidate.position_applied, prev.hours_per_week),
      hours_of_work_edited: false,
    }));
  };

  // What the offer endpoints receive. hours_of_work goes only when the form
  // could show it; the backend stores it only if it differs from the standard
  // wording for the hours on the record.
  const jobOfferPayload = () => {
    const { hours_of_work_edited, hours_of_work, ...rest } = jobOfferForm;
    return hoursOfWorkDefaults ? { ...rest, hours_of_work } : rest;
  };

  const handleSendOffer = async (e) => {
    e.preventDefault();

    const isAlreadySent = candidate.status === 'offer_sent' || candidate.offer_sent_date;

    if (isAlreadySent && !await showConfirm('Resend job offer?', e)) {
      return;
    }

    try {
      // Get email preview from backend
      const previewResponse = await candidateAPI.previewOffer(id, jobOfferPayload());

      // Show preview modal
      setEmailPreviewModal({
        isOpen: true,
        emailData: previewResponse,
        emailType: 'job_offer',
        onSendCallback: async (editedSubject, editedBody) => {
          try {
            // Send with custom content
            const resp = await candidateAPI.sendOffer(id, jobOfferPayload(), {
              subject: editedSubject,
              body: editedBody
            });
            setEmailPreviewModal({ isOpen: false, emailData: null, emailType: null, onSendCallback: null });
            setShowJobOfferModal(false);
            setJobOfferForm({
              hours_per_week: '',
              hours_per_shift: '',
              annual_salary: '',
              hourly_salary: '',
              hours_of_work: '',
              hours_of_work_edited: false,
            });
            if (resp?.in_flight) {
              showAlert('A job offer email is already being sent to this candidate — wait for it to finish (or report a failure) before sending again.', 'info');
              loadCandidate(true);
              return;
            }
            showAlert('Sending job offer email…', 'info');

            // Poll for the REAL outcome — status/offer_sent_date are set in the
            // task only on a confirmed send, so a failure must surface here.
            const outcome = await pollSendOutcome(resp?.task_id);
            if (outcome === true) {
              showAlert(`✓ Job offer email sent to ${candidate.email}`, 'success');
            } else if (outcome === false) {
              showAlert('✗ Job offer email FAILED to send — nothing was delivered to the candidate. Please try again.', 'error');
            } else {
              showAlert('Job offer email is still sending. Refresh in a moment — if the button still says "Send Job Offer", it did not send.', 'info');
            }
            // Reload candidate to get updated status
            await loadCandidate(true);
          } catch (error) {
            console.error('Error sending offer:', error);
            showAlert(describeApiError(error, 'Failed to send offer'), 'error');
          }
        }
      });
    } catch (error) {
      console.error('Error previewing offer:', error);
      showAlert(describeApiError(error, 'Failed to preview offer email'), 'error');
    }
  };

  const handleAddReference = async (e) => {
    e.preventDefault();
    try {
      await candidateAPI.addReference(id, referenceForm);
      showAlert('Reference added successfully!', 'success');
      setShowReferenceForm(false);
      setReferenceForm({
        reference_name: '',
        reference_email: '',
        reference_phone: '',
        reference_organization: '',
        reference_position: '',
        candidate_position: '',
        relationship_to_candidate: '',
        employment_start_date: '',
        employment_end_date: '',
      });
      loadCandidate(true);
    } catch (error) {
      console.error('Error adding reference:', error);
      showAlert('Failed to add reference', 'error');
    }
  };

  const handleSendReferenceRequest = async (referenceId, event) => {
    try {
      // Get email preview from backend
      const previewResponse = await candidateAPI.api.get(
        `/api/candidates/${id}/references/${referenceId}/preview-request`
      );

      // Show preview modal
      setEmailPreviewModal({
        isOpen: true,
        emailData: previewResponse.data,
        emailType: 'reference_request',
        onSendCallback: async (editedSubject, editedBody) => {
          try {
            // Send with custom content
            const resp = await candidateAPI.api.post(
              `/api/candidates/${id}/references/${referenceId}/send-request`,
              {
                subject: editedSubject,
                body: editedBody
              }
            );
            setEmailPreviewModal({ isOpen: false, emailData: null, emailType: null, onSendCallback: null });
            if (resp?.data?.in_flight) {
              showAlert('A reference request is already being sent to this referee — wait for it to finish (or report a failure) before sending again.', 'info');
              loadCandidate(true);
              return;
            }
            showAlert('Sending reference request…', 'info');

            // Poll for the REAL outcome — request_sent_date is set in the task
            // only on a confirmed send, so a failure must surface here.
            const outcome = await pollSendOutcome(resp?.data?.task_id);
            if (outcome === true) {
              showAlert('✓ Reference request sent!', 'success');
            } else if (outcome === false) {
              showAlert('✗ Reference request FAILED to send — nothing was delivered to the referee. Please try again.', 'error');
            } else {
              showAlert('Reference request is still sending. Refresh in a moment — if the "Send Request" button is still showing, it did not send.', 'info');
            }
            // Reload candidate data to update button + subtitle state
            await loadCandidate(true);
          } catch (error) {
            console.error('Error sending reference request:', error);
            showAlert('Failed to send reference request', 'error');
          }
        }
      });
    } catch (error) {
      console.error('Error previewing reference request:', error);
      showAlert('Failed to preview reference request email', 'error');
    }
  };

  const handleMarkReferenceStatus = async (referenceId, isPositive, event) => {
    const statusText = isPositive ? 'positive' : 'negative';
    if (await showConfirm(`Mark this reference as ${statusText}?`, event)) {
      try {
        await candidateAPI.api.patch(`/api/candidates/${id}/references/${referenceId}`, {
          is_positive: isPositive
        });
        showAlert(`Reference marked as ${statusText}`, 'success');
        loadCandidate(true);
      } catch (error) {
        console.error('Error updating reference:', error);
        showAlert('Failed to update reference status', 'error');
      }
    }
  };

  const handleSendDocuments = async (event) => {
    if (!await showConfirm('Send new starter documents email to candidate?', event)) return;

    try {
      // Get email preview from backend
      const previewResponse = await candidateAPI.previewDocuments(id);

      // Show preview modal
      setEmailPreviewModal({
        isOpen: true,
        emailData: previewResponse,
        emailType: 'documents',
        onSendCallback: async (editedSubject, editedBody) => {
          try {
            // Queue the send with custom content
            const resp = await candidateAPI.sendDocuments(id, {
              subject: editedSubject,
              body: editedBody
            });
            setEmailPreviewModal({ isOpen: false, emailData: null, emailType: null, onSendCallback: null });
            if (resp?.in_flight) {
              showAlert('A documents email is already being sent to this candidate — wait for it to finish (or report a failure) before sending again.', 'info');
              loadCandidate(true);
              return;
            }
            showAlert('Sending documents email…', 'info');

            // The send runs in the background; poll for the REAL outcome so a
            // failure surfaces as an error instead of a silent non-update.
            const taskId = resp && resp.task_id;
            let outcome = null; // true=sent, false=failed, null=unknown/timeout
            if (taskId) {
              for (let i = 0; i < 40; i++) { // ~60s max (downloads + up to 3 retries)
                await new Promise((r) => setTimeout(r, 1500));
                try {
                  const status = await candidateAPI.getTaskStatus(id, taskId);
                  if (status.ready) { outcome = status.successful; break; }
                } catch (e) { /* transient; keep polling */ }
              }
            }

            if (outcome === true) {
              showAlert(`✓ Documents email sent to ${candidate.email}`, 'success');
            } else if (outcome === false) {
              showAlert('✗ Documents email FAILED to send — nothing was delivered to the candidate. Please try again.', 'error');
            } else {
              showAlert('Documents email is still sending. Refresh in a moment — if the button has not turned green, it did not send.', 'info');
            }
            loadCandidate(true);
          } catch (error) {
            console.error('Error sending documents:', error);
            showAlert('Failed to send documents email', 'error');
          }
        }
      });
    } catch (error) {
      console.error('Error previewing documents email:', error);
      showAlert('Failed to preview documents email', 'error');
    }
  };

  const handleViewNightWorker = async () => {
    // Simply open the modal - the iframe will load the content
    setShowNightWorkerModal(true);
  };

  // Listen for messages from iframe (when employer assessment is submitted)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'night-worker-submitted') {
        setShowNightWorkerModal(false);
        loadCandidate(true); // Reload candidate data
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [loadCandidate]);

  if (loading) {
    return (
      <div>
        <Navigation onLogout={onLogout} onFeedback={onFeedback} />
        <div className="container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div>
        <Navigation onLogout={onLogout} onFeedback={onFeedback} />
        <div className="container">
          <p>Candidate not found</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navigation onLogout={onLogout} onFeedback={onFeedback} />
      <div className="container">
        <div className="candidate-sticky-header">
          <div className="candidate-sticky-identity">
            <h1>{`${candidate.first_name} ${candidate.last_name}`}</h1>
            <span className="candidate-sticky-position">
              {capitalizeWords(candidate.position_applied)}
            </span>
          </div>
          {/* This candidate's Drive folder. All-access roles only: scoped roles
              have no Drive access and would land on "request access". The 30px
              margin is .detail-section's padding, so the button's right edge
              lines up with the coloured part frames inside those cards. */}
          {driveDirect && (
            <button
              onClick={openDriveFolder}
              className="btn-primary"
              style={{ marginRight: '30px' }}
            >
              {`${candidate.first_name}'s Documents folder`}
            </button>
          )}
        </div>

        {candidate.archived_date && (
          /* Archived candidates stay fully editable — every workflow action below
             keeps working. This strip only explains why they're missing from the
             default list, and offers the way back. */
          <div className="archived-strip">
            <span>
              Archived {formatDate(candidate.archived_date)} · still fully editable
              {TERMINAL_STATUSES.includes(candidate.status) && (
                /* Naming the cause matters: "Restore to active" deliberately leaves
                   the status alone, so on a terminal status it is not the undo it
                   looks like. Someone marked Recruited by mistake needs the status
                   buttons, not this one — candidate 26, 2026-09-17. */
                <>
                  {' '}because their status is {statusLabel(candidate.status)}.
                  Restoring brings them back to the list but keeps that status — to
                  undo it, edit Candidate Information below and change Status.
                </>
              )}
            </span>
            <button
              onClick={handleRestore}
              className="archived-strip-action"
              disabled={restoring}
            >
              {restoring ? 'Restoring…' : 'Restore to active'}
            </button>
            {OUTCOME_COMMENT_STATUSES.includes(candidate.status) && (
              /* The outcome email can be sent (or re-sent) from here: candidates
                 rejected before the email existed, a send that failed, or someone
                 who chose "Don't send" and changed their mind. */
              <div className="archived-strip-email">
                <span>{OUTCOME_EMAIL_LABEL[candidate.status]} to the candidate:</span>
                <SendStatusSubtitle
                  sentDate={candidate.outcome_email_sent_date}
                  failedDate={candidate.outcome_email_send_failed_date}
                  attemptedDate={candidate.outcome_email_send_attempted_date}
                  sentLabel={`sent by ${senderLabel(candidate.outcome_email_sent_by)}`}
                />
                <button
                  onClick={() => openOutcomeEmail(candidate.status)}
                  className="archived-strip-action"
                  disabled={!!candidate.outcome_email_send_attempted_date && !candidate.outcome_email_sent_date && !candidate.outcome_email_send_failed_date}
                >
                  {candidate.outcome_email_sent_date ? 'Send again' : `Send ${OUTCOME_EMAIL_LABEL[candidate.status]}`}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="detail-section">
          <h2 onClick={() => toggleSection('contact')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              Candidate Information
              {expandedSections.contact && !editingInfo && (
                <button
                  onClick={(e) => { e.stopPropagation(); startEditInfo(); }}
                  className="btn-primary"
                >
                  Edit
                </button>
              )}
            </span>
            <span className="section-toggle-arrow">{expandedSections.contact ? '▼' : '▶'}</span>
          </h2>
          {expandedSections.contact && (
          <>
          {!editingInfo ? (
          <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <strong style={{ color: 'var(--color-text-muted)' }}>Email</strong>
              <span style={{ color: 'var(--color-text-muted)' }}>{candidate.email}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <strong style={{ color: 'var(--color-text-muted)' }}>Phone</strong>
              <span style={{ color: 'var(--color-text-muted)' }}>{candidate.phone || '-'}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <strong style={{ color: 'var(--color-text-muted)' }}>Position</strong>
              <span style={{ color: 'var(--color-text-muted)' }}>{capitalizeWords(candidate.position_applied)}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <strong style={{ color: 'var(--color-text-muted)' }}>Source</strong>
              <span style={{ color: 'var(--color-text-muted)' }}>{capitalizeWords(candidate.source)}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <strong style={{ color: 'var(--color-text-muted)' }}>Start Date</strong>
              <span style={{ color: 'var(--color-text-muted)' }}>{candidate.start_date ? formatDate(candidate.start_date) : '-'}</span>
            </div>
          </div>
          <div className="info-grid">
            {candidate.employment_type === 'TEMPORARY' && (
              /* Sarah's ask (2026-10-17): a temporary hire says so here, with both
                 dates and the reason, instead of only in Part 4. */
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <label>Temporary contract:</label>
                <span>
                  {candidate.start_date ? formatDate(candidate.start_date) : 'start date not set'}
                  {' to '}
                  {candidate.end_date ? formatDate(candidate.end_date) : 'end date not set'}
                  {' · '}
                  {candidate.temporary_reason || 'reason not recorded'}
                </span>
              </div>
            )}
            {candidate.hours_per_week && (
              <div className="info-item">
                <label>Hours per Week:</label>
                <span>{candidate.hours_per_week}</span>
              </div>
            )}
            {candidate.hours_per_shift && (
              <div className="info-item">
                <label>Hours per Shift / Day:</label>
                <span>{candidate.hours_per_shift}</span>
              </div>
            )}
            {candidate.hours_of_work && (
              /* Bespoke Hours of work wording can run to a paragraph, so it is
                 folded to a single line until someone opens it. */
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <label
                  onClick={() => setAmendedWordingOpen(open => !open)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  {amendedWordingOpen ? '▼' : '▶'} Amended Contract Wording
                </label>
                {amendedWordingOpen && (
                  /* A paragraph of contract prose, so smaller than the card's figures
                     and the same 13px as the edit box it was typed in. */
                  <span style={{ whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: 1.4 }}>{candidate.hours_of_work}</span>
                )}
              </div>
            )}
            {candidate.annual_salary && (
              <div className="info-item">
                <label>Annual Salary:</label>
                <span>£{candidate.annual_salary.toLocaleString()}</span>
              </div>
            )}
            {candidate.hourly_salary && (
              <div className="info-item">
                <label>Hourly Salary:</label>
                <span>£{candidate.hourly_salary}/hour</span>
              </div>
            )}
          </div>
          </>
          ) : (
          <div className="candidate-info-edit" style={{ marginBottom: '40px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>First Name *</label>
                <input type="text" value={infoForm.first_name} onChange={(e) => handleInfoChange('first_name', e.target.value)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Last Name *</label>
                <input type="text" value={infoForm.last_name} onChange={(e) => handleInfoChange('last_name', e.target.value)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Email *</label>
                <input type="email" value={infoForm.email} onChange={(e) => handleInfoChange('email', e.target.value)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Phone</label>
                <input type="text" value={infoForm.phone} onChange={(e) => handleInfoChange('phone', e.target.value)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Position</label>
                <select value={infoForm.position_applied} onChange={(e) => handleInfoChange('position_applied', e.target.value)}>
                  {POSITION_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{capitalizeWords(opt)}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Source</label>
                <select value={infoForm.source} onChange={(e) => handleInfoChange('source', e.target.value)}>
                  {SOURCE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{capitalizeWords(opt)}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Status</label>
                <select value={infoForm.status} onChange={(e) => handleInfoChange('status', e.target.value)}>
                  {STATUS_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                {/* The stages advance on their own as the workflow runs, so this is
                    for corrections rather than routine use. */}
                <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                  Normally set automatically — change it to correct a mistake.
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Hours per Week</label>
                {/* step="any": without it the input defaults to whole numbers and
                    flags a legitimate 22.5-hour week as invalid. */}
                <input type="number" min="0" step="any" value={infoForm.hours_per_week} onChange={(e) => handleInfoChange('hours_per_week', e.target.value)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Hours per Shift / Day</label>
                <input type="number" min="0" step="any" value={infoForm.hours_per_shift} onChange={(e) => handleInfoChange('hours_per_shift', e.target.value)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Annual Salary (£)</label>
                <input type="number" min="0" value={infoForm.annual_salary} onChange={(e) => handleInfoChange('annual_salary', e.target.value)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Hourly Salary (£)</label>
                <input type="text" value={infoForm.hourly_salary} onChange={(e) => handleInfoChange('hourly_salary', e.target.value)} />
              </div>
              {candidate.employment_type === 'TEMPORARY' && (
                /* Employment type and end date are changed in Part 4, where the
                   Claire notification is wired; only the reason is edited here. */
                <div style={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Reason for the temporary contract</label>
                  <textarea
                    rows={2}
                    value={infoForm.temporary_reason || ''}
                    onChange={(e) => handleInfoChange('temporary_reason', e.target.value)}
                    placeholder="e.g. maternity cover for ..."
                    style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '13px', lineHeight: 1.4 }}
                  />
                  <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                    Shown on this card and quoted to Accounts and Claire. The start and end dates are set in Part 4.
                  </span>
                </div>
              )}
              {hoursOfWorkDefaults && (
                <div style={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Hours of work (as it will appear in the contract)</label>
                  <textarea
                    rows={10}
                    value={infoForm.hours_of_work || ''}
                    onChange={(e) => handleInfoChange('hours_of_work', e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '13px', lineHeight: 1.4 }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginTop: '4px', fontSize: '12px', color: 'var(--color-text-muted)' }}>
                    <span>
                      {infoForm.hours_of_work_edited
                        ? "This candidate's own wording — it will not change if the hours or position change."
                        : "Standard wording for this position. Edit only if this candidate's shifts or weekends differ."}
                    </span>
                    {infoForm.hours_of_work_edited && (
                      <button
                        type="button"
                        onClick={resetInfoHoursOfWork}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', color: 'inherit', whiteSpace: 'nowrap', fontSize: '12px' }}
                      >
                        Reset to standard
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button onClick={(e) => saveInfo(e)} className="btn-primary" disabled={savingInfo}>
                {savingInfo ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => setEditingInfo(false)} className="btn-secondary" disabled={savingInfo}>
                Cancel
              </button>
            </div>
          </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div className="info-item">
                <label>Current Status:</label>
                {/* The reason for a Reject / Offer Declined sits to the right of the
                    badge — shown only while that is still the status, and only when
                    a comment was actually typed. */}
                <div className="status-with-comment">
                  <span className={`status-badge status-${candidate.status}`}>
                    {candidate.status === 'offer_sent' ? 'job offer sent' : candidate.status === 'candidate_signed' ? 'candidate signed contract' : candidate.status === 'recruited' ? 'Recruited !' : candidate.status === 'background_check' ? 'background checked' : candidate.status.replace(/_/g, ' ')}
                  </span>
                  {OUTCOME_COMMENT_STATUSES.includes(candidate.status) && candidate.outcome_comment && (
                    <div className="outcome-comment">
                      <span className="outcome-comment-text">“{candidate.outcome_comment}”</span>
                      <span className="outcome-comment-meta">
                        {[staffName(candidate.outcome_comment_by), candidate.outcome_comment_date && formatDate(candidate.outcome_comment_date)].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {/* Omitted entirely for candidates added before this was tracked —
                  their creator is genuinely unknown, so we say nothing. */}
              {candidate.created_by_name && (
                <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                  Added by {staffName(candidate.created_by_name)} on {formatDate(candidate.created_at)}
                </span>
              )}
              {/* The two records of a progression point at each other. */}
              {candidate.progressed_to && (
                <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                  Progressed to {candidate.progressed_to.position || 'RVN'}
                  {candidate.progressed_to.created_at ? ` on ${formatDate(candidate.progressed_to.created_at)}` : ''} ·{' '}
                  <button type="button" className="btn-link-small btn-link-view" onClick={() => navigate(`/candidates/${candidate.progressed_to.id}`)}>
                    open that record
                  </button>
                </span>
              )}
              {isProgression && (
                <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                  Progression from {candidate.progressed_from_position || 'a previous position'}
                  {candidate.progressed_from_candidate_id && (
                    <>
                      {' '}·{' '}
                      <button type="button" className="btn-link-small btn-link-view" onClick={() => navigate(`/candidates/${candidate.progressed_from_candidate_id}`)}>
                        open the original record
                      </button>
                    </>
                  )}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {canProgress && (
                <button
                  onClick={handleProgress}
                  className="btn-primary btn-outline-yellow"
                  disabled={progressing}
                  title="They have qualified: start the RVN contract on a new linked record"
                >
                  {progressing ? 'Creating…' : 'Progress to RVN'}
                </button>
              )}
              <button
                onClick={(e) => handleStatusUpdate('recruited', e)}
                className={`btn-success${glowIf('top:recruited')}`}
              >
                {candidate.status === 'recruited' ? 'Recruited !' : 'Mark as Recruited'}
              </button>
              {/* The candidate turned the job down. Terminal and archiving like
                  Recruited and Rejected, so it sits with them, confirms first, and
                  then reads as a state label rather than staying clickable. */}
              <button
                onClick={(e) => handleStatusUpdate('offer_declined', e)}
                className="btn-primary btn-outline-amber"
                disabled={candidate.status === 'offer_declined'}
                title="The candidate turned the job down"
              >
                {candidate.status === 'offer_declined'
                  ? 'Job Offer Declined'
                  : 'Mark as Offer Declined'}
              </button>
              <button
                onClick={(e) => handleStatusUpdate('rejected', e)}
                className="btn-danger"
              >
                Reject
              </button>
            </div>
          </div>
          </>
          )}
        </div>

        <div className="detail-section">
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'var(--color-bg-hover)', borderRadius: '8px', border: `2px solid ${PART1A_COLOR}`, '--glow-color': PART1A_COLOR }}>
          <h2 onClick={() => setExpandedSections(prev => ({ ...prev, checklist: !part1Open }))} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: part1Open ? '15px' : '0', color: PART1A_COLOR, fontWeight: 'bold' }}>
            <span>PART 1 - Interview</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {part1Complete && (
                <span style={{ fontWeight: '600', color: PART1A_COLOR }}>Completed</span>
              )}
              <span className="section-toggle-arrow">{part1Open ? '▼' : '▶'}</span>
            </div>
          </h2>
          {part1Open && (
          <>
            <div className="interview-steps">
              {!isProgression && (<>
              {/* 1. CV + cover letter: a header row (tick by hand, or an upload ticks it)
                  plus one line per file, so the row never wraps whatever has arrived */}
              <div className="interview-step-group">
                <div className="interview-row">
                  {renderToggleMark(candidate.cv_received === true, () => handleChecklistUpdate('cv_received', !candidate.cv_received), 'CV and cover letter received')}
                  <div className="interview-step-label">
                    <span className="interview-toggle-label interview-step-title" onClick={() => handleChecklistUpdate('cv_received', !candidate.cv_received)}><span className="interview-step-no">1.</span><span>CV and cover letter</span></span>
                  </div>
                  <div className="interview-step-detail" />
                  <div className="interview-step-actions">
                    <button
                      className={`btn-primary btn-outline-yellow interview-import-btn${glowIf('p1:cv')}`}
                      onClick={openEmailAttachModal}
                      disabled={uploadingKind !== null}
                      title="Pick the CV or cover letter straight out of an email in your inbox, HR's or the department heads'"
                    >
                      {/* "again" only once a file is there — before that, nothing has been
                          imported. "Imported" once BOTH files came from an email (a Replace
                          by upload sets the source back to 'upload'). */}
                      {candidate.cv_file_id || candidate.cover_letter_file_id
                        ? <ResendLabel
                            label={candidate.cv_source === 'email' && candidate.cover_letter_source === 'email'
                              ? 'Imported from email'
                              : 'Import from email'}
                            again="Import again"
                          />
                        : 'Import from email'}
                    </button>
                  </div>
                </div>
                {renderFileRow('cv', 'CV', candidate.cv_file_id, cvInputRef)}
                {renderFileRow('cover_letter', 'Cover letter', candidate.cover_letter_file_id, coverLetterInputRef)}
              </div>

              {/* 2. Screening — same field, different name per team */}
              <div className="interview-step interview-row">
                {renderToggleMark(candidate.background_check_passed === true, () => handleChecklistUpdate('background_check_passed', !candidate.background_check_passed), isReception ? 'Social media check done' : 'Background checks performed', 'p1:screening')}
                <div className="interview-step-label">
                  <span className="interview-toggle-label interview-step-title" onClick={() => handleChecklistUpdate('background_check_passed', !candidate.background_check_passed)}>
                    <span className="interview-step-no">{interviewSteps.screening}.</span>
                    <span>{isReception ? 'Social media check' : 'Background checks'}</span>
                  </span>
                </div>
                <div className="interview-step-detail" />
                <div className="interview-step-actions" />
              </div>

              </>)}
              {/* 3. Phone interview — reception only: a casual call to decide whether to invite them in */}
              {isReception && (
                <div className="interview-step interview-row">
                  {renderOutcomeMark(candidate.phone_interview_passed, 'phone_interview_passed', 'Phone interview', 'Invite to interview', 'Not invited')}
                  <div className="interview-step-label interview-step-title">
                    <span className="interview-step-no">{interviewSteps.phone}.</span>
                    <span>
                      Phone interview
                      {renderBookLink('phone-interview', candidate.phone_interview_date, 'phone interview')}
                    </span>
                  </div>
                  <div className="interview-step-detail">
                    <span>{[
                      candidate.phone_interview_date ? `Booked for ${formatDateTime(candidate.phone_interview_date)}` : null,
                      outcomeWords(candidate.phone_interview_passed, 'Invite to interview', 'Not invited'),
                    ].filter(Boolean).join(' · ') || 'Casual call — invite them in, or not?'}</span>
                  </div>
                  <div className="interview-step-actions" />
                </div>
              )}

              {/* Interview — the form is a copy of the team's questions Doc, filled in during it */}
              <div className="interview-step interview-row">
                {renderOutcomeMark(candidate.in_person_interview_passed, 'in_person_interview_passed', 'In person interview')}
                <div className="interview-step-label interview-step-title">
                  <span className="interview-step-no">{interviewSteps.interview}.</span>
                  <span>
                    In person interview
                    {renderBookLink('in-person-interview', candidate.in_person_interview_date, 'interview')}
                  </span>
                </div>
                <div className="interview-step-detail">
                  <span>{[
                    candidate.in_person_interview_date ? `Booked for ${formatDateTime(candidate.in_person_interview_date)}` : null,
                    hasInterviewForm && candidate.interview_form_doc_id ? '✓ Form in the candidate\'s Documents folder' : null,
                    outcomeWords(candidate.in_person_interview_passed),
                  ].filter(Boolean).join(' · ') || (isProgression ? 'Re-interview with the SVN/RVN form' : hasInterviewForm ? '' : 'No interview form for this position')}</span>
                </div>
                <div className="interview-step-actions">
                  {hasInterviewForm && (
                    candidate.interview_form_doc_id ? (
                      <span className="interview-link-pair">
                        <button className="btn-link-small btn-link-view" onClick={() => window.open(interviewFormUrl, '_blank')}>
                          Open form
                        </button>
                        <span className="interview-link-dot">·</span>
                        <button
                          className="btn-link-small btn-link-replace"
                          onClick={handleCreateInterviewForm}
                          disabled={creatingInterviewForm}
                          title="Start again with a blank copy of the questions"
                        >
                          {creatingInterviewForm ? 'Creating…' : 'New form'}
                        </button>
                      </span>
                    ) : (
                      <button className={`btn-primary btn-outline-yellow interview-import-btn${glowIf('p1:interview_form')}`} onClick={handleCreateInterviewForm} disabled={creatingInterviewForm}>
                        {creatingInterviewForm ? 'Creating…' : 'Open interview form'}
                      </button>
                    )
                  )}
                </div>
              </div>

              {!isProgression && (<>
              {/* Trial */}
              <div className="interview-step interview-row">
                {renderOutcomeMark(candidate.trial_day_passed, 'trial_day_passed', trialLabel)}
                <div className="interview-step-label interview-step-title">
                  <span className="interview-step-no">{interviewSteps.trial}.</span>
                  <span>
                    {trialLabel}
                    {renderBookLink('trial-day', candidate.trial_day_date, trialLabel.toLowerCase())}
                  </span>
                </div>
                <div className="interview-step-detail">
                  <span>{[
                    candidate.trial_day_date ? `Booked for ${formatDateTime(candidate.trial_day_date)}` : null,
                    outcomeWords(candidate.trial_day_passed),
                  ].filter(Boolean).join(' · ') || (isReception ? 'Usually 2–3 hours' : 'Usually a full day')}</span>
                </div>
                <div className="interview-step-actions" />
              </div>
              </>)}
            </div>

            {!isProgression && (<>
            {/* Job description email — kept, but nobody uses it, so it is folded away */}
            <div
              className="interview-optional-toggle"
              onClick={() => setExpandedSections(prev => ({ ...prev, jobDescription: !jdOpen }))}
            >
              {jdOpen ? '▼' : '▶'} Job description email (optional)
              {candidate.job_description_sent_date ? ' — sent' : ''}
            </div>
            {jdOpen && (
              /* Unfolded: the dropdown starts under the step numbers, and the Send
                 button sits beneath it on the same left edge, in the pane's yellow. */
              <div className="interview-jd">
                <select
                  className="interview-jd-select"
                  value={candidate.job_description_file_id || ''}
                  onChange={handleJobDescriptionChange}
                  disabled={jobDescriptionsLoading}
                >
                  <option value="" disabled>
                    {jobDescriptionsLoading ? 'Loading...' : 'Choose a Job Description'}
                  </option>
                  {jobDescriptions.map(jd => (
                    <option key={jd.id} value={jd.id}>
                      {jd.name}
                    </option>
                  ))}
                </select>
                {candidate.job_description_file_id && (
                  <div className="interview-jd-send">
                    <button
                      onClick={handleSendJobDescriptionEmail}
                      className={`btn-primary ${candidate.job_description_sent_date ? 'btn-fill-yellow' : 'btn-outline-yellow'}`}
                      disabled={candidate.job_description_sent_date}
                    >
                      {candidate.job_description_sent_date ? "J.D. Email Sent" : "Send J.D. Email"}
                    </button>
                    <SendStatusSubtitle
                      sentDate={candidate.job_description_sent_date}
                      failedDate={candidate.job_description_send_failed_date}
                      attemptedDate={candidate.job_description_send_attempted_date}
                    />
                  </div>
                )}
              </div>
            )}
            </>)}
          </>
          )}
          </div>
        </div>

        {/* Part 2 does not exist for a progression: no offer letter, no references. */}
        {!isProgression && (
        <div className="detail-section">
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'var(--color-bg-hover)', borderRadius: '8px', border: `2px solid #f05f57`, '--glow-color': '#f05f57' }}>
            <h2 onClick={() => toggleSection('references')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (expandedSections.references !== false && (expandedSections.references === true || !(part2Complete && !partHasNext('p2')))) ? '15px' : '0', color: '#f05f57', fontWeight: 'bold' }}>
              <span>PART 2 - References</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {(part2Complete) && (
                  <span style={{ fontWeight: '600', color: '#f05f57' }}>Completed</span>
                )}
                <span className="section-toggle-arrow">{(expandedSections.references !== false && (expandedSections.references === true || !(part2Complete && !partHasNext('p2')))) ? '▼' : '▶'}</span>
              </div>
            </h2>

            {(expandedSections.references !== false && (expandedSections.references === true || !(part2Complete && !partHasNext('p2')))) && (
            <>
            {/* alignItems: flex-start, or the buttons that carry no status
                subtitle stretch to the height of the one that does — a flex row
                defaults to align-items: stretch, which made "Add Reference
                Manually" noticeably taller than its neighbours. */}
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <button
                  onClick={handleOpenJobOfferModal}
                  className={`btn-danger${glowIf('p2:send_offer')}`}
                >
                  {candidate.status === 'offer_sent' || candidate.offer_sent_date ? <ResendLabel label="Job Offer Sent" /> : 'Send Job Offer'}
                </button>
                <SendStatusSubtitle
                  sentDate={candidate.offer_sent_date}
                  failedDate={candidate.offer_send_failed_date}
                  attemptedDate={candidate.offer_send_attempted_date}
                />
                {/* Acceptance is stamped by the referee form or by sending the
                    documents pack; this mark is for a yes that came by phone or
                    email (the no-reference positions), and shows the date either way. */}
                {candidate.offer_sent_date && (
                  <div className="offer-accepted-row">
                    {renderToggleMark(!!candidate.offer_accepted_date, toggleOfferAccepted, 'Offer accepted', 'p2:accept')}
                    <span className="interview-toggle-label" onClick={toggleOfferAccepted}>
                      {candidate.offer_accepted_date
                        ? `Offer accepted · ${formatDate(candidate.offer_accepted_date)}`
                        : 'Offer accepted?'}
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowReferenceForm(!showReferenceForm)}
                className="btn-success part1-action-btn"
              >
                {showReferenceForm ? 'Cancel' : 'Add Reference Manually'}
              </button>
            </div>

          {showReferenceForm && (
            <form onSubmit={handleAddReference} className="reference-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Reference Name *</label>
                  <input
                    type="text"
                    value={referenceForm.reference_name}
                    onChange={(e) =>
                      setReferenceForm({ ...referenceForm, reference_name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Reference Email *</label>
                  <input
                    type="email"
                    value={referenceForm.reference_email}
                    onChange={(e) =>
                      setReferenceForm({ ...referenceForm, reference_email: e.target.value })
                    }
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={referenceForm.reference_phone}
                    onChange={(e) =>
                      setReferenceForm({ ...referenceForm, reference_phone: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Organization</label>
                  <input
                    type="text"
                    value={referenceForm.reference_organization}
                    onChange={(e) =>
                      setReferenceForm({ ...referenceForm, reference_organization: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Reference Position</label>
                  <input
                    type="text"
                    value={referenceForm.reference_position}
                    onChange={(e) =>
                      setReferenceForm({ ...referenceForm, reference_position: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Candidate Position</label>
                  <input
                    type="text"
                    value={referenceForm.candidate_position}
                    onChange={(e) =>
                      setReferenceForm({ ...referenceForm, candidate_position: e.target.value })
                    }
                    placeholder="Candidate's position at the time"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Relationship to Candidate</label>
                  <input
                    type="text"
                    value={referenceForm.relationship_to_candidate}
                    onChange={(e) =>
                      setReferenceForm({
                        ...referenceForm,
                        relationship_to_candidate: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Employment Start Date</label>
                  <input
                    type="text"
                    value={referenceForm.employment_start_date}
                    onChange={(e) =>
                      setReferenceForm({ ...referenceForm, employment_start_date: e.target.value })
                    }
                    placeholder="e.g. January 2020"
                  />
                </div>
                <div className="form-group">
                  <label>Employment End Date</label>
                  <input
                    type="text"
                    value={referenceForm.employment_end_date}
                    onChange={(e) =>
                      setReferenceForm({ ...referenceForm, employment_end_date: e.target.value })
                    }
                    placeholder="e.g. December 2023"
                  />
                </div>
              </div>
              <button type="submit" className="btn-primary">
                Add Reference
              </button>
            </form>
          )}

          {candidate.references && candidate.references.length > 0 ? (
            <div className="references-list">
              {candidate.references.map((reference) => (
                <div key={reference.id} className="reference-card">
                  <div className="reference-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <h3 style={{ margin: 0 }}>{reference.reference_name}</h3>
                      {!reference.request_sent_date && (
                        <button
                          type="button"
                          onClick={(e) => handleSendReferenceRequest(reference.id, e)}
                          className={`btn-small${glowIf(`p2:ref_send_${reference.id}`)}`}
                        >
                          Send Request
                        </button>
                      )}
                      <SendStatusSubtitle
                        sentDate={reference.request_sent_date}
                        failedDate={reference.request_send_failed_date}
                        attemptedDate={reference.request_send_attempted_date}
                        sentLabel="Request sent"
                        glow={isWaiting(`p2:ref_${reference.id}`)}
                      />
                    </div>
                    <div>
                      {(reference.reference_content || reference.attachment_file_id) && (() => {
                        let content = reference.reference_content || '';
                        // Remove quoted reply patterns
                        const patterns = [
                          /--+\s*Original Message\s*--+[\s\S]*/i,
                          /On .{10,50} wrote:[\s\S]*/i,
                          /_{5,}[\s\S]*From:[\s\S]*/i,
                          /-{5,}[\s\S]*From:[\s\S]*/i,
                          /\n>.*(\n>.*)*$/,
                          /From:.*\nSent:.*\nTo:.*\nSubject:[\s\S]*/i,
                          /From:.*\nDate:.*\nTo:.*\nSubject:[\s\S]*/i,
                        ];
                        for (const pattern of patterns) {
                          content = content.split(pattern)[0];
                        }
                        content = content.trim();
                        // A reference sent as an attachment: the body is only "please
                        // find attached", so the tooltip names the file instead, and
                        // clicking the badge opens it (saved in the candidate's Drive
                        // folder by the reference monitor).
                        const attachment = reference.attachment_file_id;
                        const showBody = content && !(attachment && /attach/i.test(content));
                        const openAttachment = () => openCandidateDocument(
                          'reference',
                          `https://drive.google.com/file/d/${attachment}/view`,
                          String(reference.id)
                        );
                        return (
                          <div
                            className={`reference-received-badge${attachment ? ' has-attachment' : ''}`}
                            onClick={attachment ? openAttachment : undefined}
                          >
                            <span>Reference Received</span>
                            <span className="reference-received-subtext">
                              {attachment ? 'click to open' : 'check here'}
                            </span>
                            <div className="reference-received-tooltip">
                              <div className="tooltip-from">From: {reference.reference_email}</div>
                              {attachment && (
                                <div className="tooltip-attachment">
                                  📎 Sent as an attachment: <strong>{reference.attachment_filename || 'reference'}</strong>
                                  <div className="tooltip-attachment-hint">Click the badge to open it.</div>
                                </div>
                              )}
                              {showBody && <div className="tooltip-body">{content}</div>}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <p>
                    <strong>Email:</strong> {reference.reference_email}
                  </p>
                  {reference.reference_phone && (
                    <p>
                      <strong>Phone:</strong> {reference.reference_phone}
                    </p>
                  )}
                  {reference.reference_organization && (
                    <p>
                      <strong>Organization:</strong> {reference.reference_organization}
                    </p>
                  )}
                  {reference.reference_position && (
                    <p>
                      <strong>Reference Previous Position:</strong> {reference.reference_position}
                    </p>
                  )}
                  {reference.candidate_position && (
                    <p>
                      <strong>Candidate Position:</strong> {reference.candidate_position}
                    </p>
                  )}
                  <p>
                    <strong>Status:</strong>{' '}
                    <span className="reference-status-badge">
                      {reference.response_received_date
                        ? 'Response Received'
                        : reference.request_sent_date
                        ? 'Reference Requested'
                        : 'Reference Not Requested'}
                    </span>
                  </p>
                  {reference.response_received_date && reference.is_positive === null && (
                    <div className="reference-actions">
                      <button
                        onClick={(e) => handleMarkReferenceStatus(reference.id, true, e)}
                        className={`btn-small btn-success${glowIf(`p2:ref_mark_${reference.id}`)}`}
                      >
                        Mark Positive
                      </button>
                      <button
                        onClick={(e) => handleMarkReferenceStatus(reference.id, false, e)}
                        className={`btn-small btn-danger${glowIf(`p2:ref_mark_${reference.id}`)}`}
                      >
                        Mark Negative
                      </button>
                    </div>
                  )}
                  {reference.is_positive === true && (
                    <span className="reference-verdict reference-positive">Positive Reference</span>
                  )}
                  {reference.is_positive === false && (
                    <span className="reference-verdict reference-negative">Negative Reference</span>
                  )}
                </div>
              ))}
            </div>
          ) : null}

            {!(candidate.references && candidate.references.length > 0) && (
              /* Muted, matching the "Not yet sent" send-status subtitles: it
                 reports a state, it does not ask for anything. */
              <p className={isWaiting('p2:referees') ? 'next-glow-text next-glow-tint' : undefined} style={{ margin: '20px 0 0', fontSize: '14px', color: 'var(--color-text-muted)' }}>
                No references added or received yet.{isWaiting('p2:referees') ? ' Waiting for the candidate to send referee details.' : ''}
              </p>
            )}
            </>
            )}
          </div>
        </div>

        )}
        <div className="detail-section">
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'var(--color-bg-hover)', borderRadius: '8px', border: `2px solid #288bcd`, '--glow-color': '#288bcd' }}>
            <h2 onClick={() => toggleSection('newStarterDocs')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (expandedSections.newStarterDocs !== false && (expandedSections.newStarterDocs === true || !(areAllDocumentsReceived(candidate) && !partHasNext('p3')))) ? '15px' : '0', color: '#288bcd', fontWeight: 'bold' }}>
              <span>PART 3 - New Starter documents</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {areAllDocumentsReceived(candidate) && (
                  <span style={{ fontWeight: '600', color: '#288bcd' }}>Completed</span>
                )}
                <span className="section-toggle-arrow">{(expandedSections.newStarterDocs !== false && (expandedSections.newStarterDocs === true || !(areAllDocumentsReceived(candidate) && !partHasNext('p3')))) ? '▼' : '▶'}</span>
              </div>
            </h2>
            {(expandedSections.newStarterDocs !== false && (expandedSections.newStarterDocs === true || !(areAllDocumentsReceived(candidate) && !partHasNext('p3')))) && (
            <>
            {!isProgression && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <button
                  type="button"
                  onClick={handleSendDocuments}
                  className={`btn-blue${glowIf('p3:send_docs')}`}
                >
                  {candidate.documents_sent_date ? <ResendLabel label="Documents Email Sent" /> : 'Send Documents Email'}
                </button>
                {candidate.documents_sent_date ? (
                  <span className={isWaiting('p3:docs') ? 'next-glow-text next-glow-tint' : undefined} style={{ fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'center' }}>
                    {formatDateTime(candidate.documents_sent_date)} · Confirmed as sent{isWaiting('p3:docs') ? ' · waiting for the candidate' : ''}
                  </span>
                ) : candidate.documents_send_failed_date ? (
                  <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: '600', textAlign: 'center' }}>
                    ✗ Last send failed {formatDateTime(candidate.documents_send_failed_date)} — not delivered
                  </span>
                ) : candidate.documents_send_attempted_date ? (
                  <span style={{ fontSize: '12px', color: '#b45309', textAlign: 'center' }}>
                    Sending… (attempted {formatDateTime(candidate.documents_send_attempted_date)})
                  </span>
                ) : (
                  <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'center' }}>
                    Not yet sent
                  </span>
                )}
              </div>

              {/* Stacked beside the button, matching Part 6. Read from what the send
                  actually recorded: this email does NOT abort on a failed Drive
                  download, so a document can be missing from a mail that still went
                  out, and ticking a fixed four would claim otherwise. Sends made
                  before the record existed fall back to the intended four (verified
                  from Gmail on 2026-08-29 — every one of them did carry all four). */}
              {candidate.documents_sent_date && (
                <div style={{ maxWidth: '280px' }}>
                  <span style={{ fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '13px' }}>Attached:</span>
                  {(candidate.documents_attached
                    || ['Employee Handbook', 'Health & Safety Manual', 'Data Protection Policy', 'Terms & Conditions']
                         .map((name) => ({ name, attached: true }))
                  ).map((doc) => (
                    <div key={doc.name} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '13px', marginTop: '2px' }}>
                      <span style={{ color: doc.attached ? '#16a34a' : '#dc2626' }}>{doc.attached ? '✓' : '✗'}</span>
                      <span style={{ color: doc.attached ? 'var(--color-text-primary)' : '#dc2626' }}>
                        {doc.name}{doc.attached ? '' : ' — not attached'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            )}
            {(candidate.documents_sent_date || isProgression) && (
            <>
              {isProgression && (
                <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '15px' }}>
                  Medical, HMRC, personnel record and ID were collected on the original record and carried over.
                  This part asks only for the RCVS registration and proof of qualification.
                </p>
              )}
              {!isProgression && (<>

              <h3 style={{ marginBottom: '15px', color: '#288bcd' }}>Forms to Complete</h3>

                <div className="checklist-item" style={{ marginBottom: '12px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #288bcd' }}>
                  <input
                    type="checkbox"
                    id="medical-form"
                    checked={candidate.medical_form_received === true}
                    onChange={(e) => handleChecklistUpdate('medical_form_received', e.target.checked)}
                  />
                  <label htmlFor="medical-form">Medical Questionnaire</label>
                  {candidate.medical_condition_flagged && (
                    <span className="flag-badge flag-warning">Condition Flagged</span>
                  )}
                  {candidate.medical_form_received && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button
                        onClick={() => setShowMedicalModal(true)}
                        className="btn-small btn-blue"
                      >
                        View
                      </button>
                      <span className="flag-badge flag-received">RECEIVED</span>
                    </div>
                  )}
                </div>

                <div className="checklist-item" style={{ marginBottom: '12px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #288bcd' }}>
                  <input
                    type="checkbox"
                    id="hmrc-form"
                    checked={candidate.hmrc_form_received === true}
                    onChange={(e) => handleChecklistUpdate('hmrc_form_received', e.target.checked)}
                  />
                  <label htmlFor="hmrc-form">HMRC Starter Checklist</label>
                  <AccountsSentTick notifiedDate={candidate.accounts_notified_date} accountsName={accountsName} />
                  {candidate.hmrc_form_received && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button
                        onClick={() => setShowHmrcModal(true)}
                        className="btn-small btn-blue"
                      >
                        View
                      </button>
                      <span className="flag-badge flag-received">RECEIVED</span>
                    </div>
                  )}
                </div>

                <div className="checklist-item" style={{ marginBottom: '12px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #288bcd' }}>
                  <input
                    type="checkbox"
                    id="personal-record"
                    checked={candidate.personal_record_received === true}
                    onChange={(e) => handleChecklistUpdate('personal_record_received', e.target.checked)}
                  />
                  <label htmlFor="personal-record">Personal Record Form</label>
                  <AccountsSentTick notifiedDate={candidate.accounts_notified_date} accountsName={accountsName} />
                  {candidate.personal_record_received && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button
                        onClick={() => setShowPersonalRecordModal(true)}
                        className="btn-small btn-blue"
                      >
                        View
                      </button>
                      <span className="flag-badge flag-received">RECEIVED</span>
                    </div>
                  )}
                </div>

                <div className="checklist-item" style={{ marginBottom: '12px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #288bcd' }}>
                  <input
                    type="checkbox"
                    id="unum-form"
                    checked={candidate.unum_form_received === true}
                    onChange={(e) => handleChecklistUpdate('unum_form_received', e.target.checked)}
                  />
                  <label htmlFor="unum-form">Unum Enrollment</label>
                  {candidate.unum_form_received && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button
                        onClick={() => setShowUnumModal(true)}
                        className="btn-small btn-blue"
                      >
                        View
                      </button>
                      <span className="flag-badge flag-received">RECEIVED</span>
                    </div>
                  )}
                </div>
                {/* Must match settings.NIGHT_WORKER_POSITIONS on the backend, which
                    decides who is emailed the night-worker questionnaire link. It
                    said ooh_veterinary_nurse (not a real position) instead of
                    rvn_night_nurse, so an RVN Night Nurse was sent the form but had
                    nowhere to record it. */}
                {['ooh_veterinary_surgeon', 'rvn_night_nurse'].includes(candidate.position_applied) && (
                  <div className="checklist-item" style={{ marginBottom: '12px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #288bcd' }}>
                    <input
                      type="checkbox"
                      id="night-worker-form"
                      checked={candidate.night_worker_form_received === true}
                      onChange={(e) => handleChecklistUpdate('night_worker_form_received', e.target.checked)}
                    />
                    <label htmlFor="night-worker-form">Night Worker Health Questionnaire</label>
                    {candidate.night_worker_form_received && (
                      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button
                          onClick={handleViewNightWorker}
                          className={`btn-small ${candidate.night_worker_assessment_completed ? 'btn-success' : 'btn-danger'}${glowIf('p3:night')}`}
                        >
                          {candidate.night_worker_assessment_completed ? 'Assessment Completed' : 'Complete Assessment'}
                        </button>
                        <span className="flag-badge flag-received">RECEIVED</span>
                      </div>
                    )}
                  </div>
                )}

              </>)}

              <h3 style={{ marginBottom: '15px', color: '#288bcd' }}>{isProgression ? 'RCVS registration and proof of qualification' : 'Required Documents'}</h3>

                {!isProgression && (
                <div className="checklist-item" style={{ marginBottom: '12px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #288bcd' }}>
                  <input
                    type="checkbox"
                    id="id-document"
                    checked={candidate.id_document_received === true}
                    onChange={(e) => handleChecklistUpdate('id_document_received', e.target.checked)}
                  />
                  <label htmlFor="id-document">ID Document (Driving Licence or Passport)</label>
                  {candidate.id_document_received && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {candidate.id_document_file_id && (
                        <button
                          onClick={() => setShowIdDocumentModal(true)}
                          className="btn-small btn-blue"
                        >
                          View
                        </button>
                      )}
                      <span className="flag-badge flag-received">RECEIVED</span>
                    </div>
                  )}
                </div>

                )}
                {['veterinary_surgeon', 'ooh_veterinary_surgeon', 'zero_hours_vet', 'rvn', 'rvn_night_nurse'].includes(candidate.position_applied.toLowerCase()) && (
                  <>
                    <div className="checklist-item" style={{ marginBottom: '12px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #288bcd' }}>
                      <input
                        type="checkbox"
                        id="proof-qualification"
                        checked={candidate.proof_of_qualification_received === true}
                        onChange={(e) => handleChecklistUpdate('proof_of_qualification_received', e.target.checked)}
                      />
                      <label htmlFor="proof-qualification">Proof of Qualification</label>
                      {candidate.proof_of_qualification_received && (
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {candidate.proof_of_qualification_file_id && (
                            <button
                              onClick={() => setShowQualificationModal(true)}
                              className="btn-small btn-blue"
                            >
                              View
                            </button>
                          )}
                          <span className="flag-badge flag-received">RECEIVED</span>
                        </div>
                      )}
                    </div>

                    {!isProgression && (
                    <div className="checklist-item" style={{ marginBottom: '12px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #288bcd' }}>
                      <input
                        type="checkbox"
                        id="cpd-record"
                        checked={candidate.cpd_record_received === true}
                        onChange={(e) => handleChecklistUpdate('cpd_record_received', e.target.checked)}
                      />
                      <label htmlFor="cpd-record">CPD Record</label>
                      {candidate.cpd_record_received && (
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {candidate.cpd_record_file_id && (
                            <button
                              onClick={() => setShowCpdRecordModal(true)}
                              className="btn-small btn-blue"
                            >
                              View
                            </button>
                          )}
                          <span className="flag-badge flag-received">RECEIVED</span>
                        </div>
                      )}
                    </div>

                    )}
                    <div className="checklist-item" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '12px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #288bcd' }}>
                      <label htmlFor="rcvs-number" style={{ marginBottom: '8px' }}>RCVS Number</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="text"
                          id="rcvs-number"
                          value={candidate.rcvs_number || ''}
                          onChange={(e) => {
                            handleChecklistUpdate('rcvs_number', e.target.value);
                            setRcvsVerificationFailed(false);
                          }}
                          placeholder="Enter RCVS number"
                          style={{
                            width: '200px',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid var(--color-border)',
                            backgroundColor: 'var(--color-bg-card)',
                            color: 'var(--color-text-light)'
                          }}
                        />
                        {candidate.rcvs_number && (
                          <button
                            onClick={candidate.rcvs_verified ? () => setShowRcvsResultsModal(true) : handleVerifyRcvs}
                            disabled={rcvsVerifying}
                            className={`btn-small ${candidate.rcvs_verified ? 'btn-success' : (rcvsVerificationFailed ? 'btn-danger' : (rcvsVerifying ? 'btn-secondary' : 'btn-success'))}${glowIf('p3:rcvs')}`}
                            style={{
                              padding: '6px 12px',
                              fontSize: '13px',
                              cursor: rcvsVerifying ? 'not-allowed' : 'pointer',
                              opacity: rcvsVerifying ? 0.8 : 1
                            }}
                          >
                            {candidate.rcvs_verified ? '✓ Verified! (View)' : (rcvsVerificationFailed ? '✗ Verification Failed' : (rcvsVerifying ? 'Verifying...' : 'Verify on RCVS'))}
                          </button>
                        )}
                      </div>
                      {candidate.rcvs_number && !candidate.rcvs_verified && (
                        <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '6px', marginLeft: '0' }}>
                          Click "Verify on RCVS" to automatically check this number on the RCVS website
                        </p>
                      )}
                      {isProgression && !candidate.rcvs_verified && (
                        <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '6px', marginLeft: '0' }}>
                          A student enrolment number is not a registration — enter the number the RCVS issued on registration, then verify it.
                          A student is not on the public register until registered, so the check fails until then.
                        </p>
                      )}
                      {candidate.rcvs_verified && candidate.rcvs_verified_date && (
                        <p style={{ fontSize: '12px', color: '#28a745', marginTop: '6px', marginLeft: '0' }}>
                          Verified on {formatDate(candidate.rcvs_verified_date)}
                        </p>
                      )}
                    </div>
                  </>
                )}

              {candidate.medical_condition_flagged && (
                <div style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--color-bg-hover)', borderRadius: '8px', borderLeft: '4px solid #ffc107' }}>
                  <h4 style={{ color: '#ffc107', marginBottom: '10px' }}>Medical Condition Flagged</h4>
                  <div className="checklist-item">
                    <input
                      type="checkbox"
                      id="medical-interview"
                      className={isNext('p3:medical') ? 'next-glow' : undefined}
                      checked={candidate.medical_interview_scheduled === true}
                      onChange={(e) => handleChecklistUpdate('medical_interview_scheduled', e.target.checked)}
                    />
                    <label htmlFor="medical-interview">Medical Interview Scheduled</label>
                  </div>
                </div>
              )}
            </>
            )}
            </>
            )}
          </div>
        </div>

        <div className="detail-section">
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'var(--color-bg-hover)', borderRadius: '8px', border: `2px solid #28a745`, '--glow-color': '#28a745' }}>
            <h2 onClick={() => toggleSection('contractLetter')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (expandedSections.contractLetter !== false && (expandedSections.contractLetter === true || !candidate.candidate_signed_date)) ? '15px' : '0', color: '#28a745', fontWeight: 'bold' }}>
              <span>PART 4 - Contract letter</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {candidate.contract_signed_date ? (
                  <span style={{ fontWeight: '600', color: '#28a745' }}>Completed</span>
                ) : candidate.candidate_signed_date ? (
                  <span className={isWaiting('p4:countersign') ? 'next-glow-text' : undefined} style={{ fontWeight: '600', color: '#28a745' }}>Candidate signed contract</span>
                ) : null}
                <span className="section-toggle-arrow">{(expandedSections.contractLetter !== false && (expandedSections.contractLetter === true || !candidate.candidate_signed_date)) ? '▼' : '▶'}</span>
              </div>
          </h2>
          {(expandedSections.contractLetter !== false && (expandedSections.contractLetter === true || !candidate.candidate_signed_date)) && (
          <>
            {/* Step 1: Send Contract Email to Candidate */}
            <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'var(--color-bg-hover)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, color: 'var(--color-text-light)' }}>Step 1: Send email to candidate</h3>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                  <button
                    type="button"
                    onClick={handleSendContractEmail}
                    className={`btn-primary${glowIf('p4:send_contract')}`}
                    disabled={!candidate.start_date}
                    style={{ opacity: !candidate.start_date ? 0.5 : 1 }}
                  >
                    {candidate.contract_sent_date ? <ResendLabel label="✓ Contract Email Sent" /> : 'Send Contract Email'}
                  </button>
                  <SendStatusSubtitle
                    sentDate={candidate.contract_sent_date}
                    failedDate={candidate.contract_send_failed_date}
                    attemptedDate={candidate.contract_send_attempted_date}
                  />
                </div>
              </div>

              {/* Contract details table (borders use the Part 4 pane colour) */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '5px' }}>
                <tbody>
                  {/* Employment Type */}
                  <tr>
                    <td style={PART3_TD_LABEL}>Employment Type</td>
                    <td style={PART3_TD_VALUE}>
                      <select
                        value={candidate.employment_type || 'PERMANENT'}
                        onChange={(e) => {
                          const newType = e.target.value;
                          handleChecklistUpdate('employment_type', newType);
                          if (newType === 'PERMANENT') {
                            // Clear end date when switching to permanent
                            handleChecklistUpdate('end_date', null);
                          }
                        }}
                        style={{
                          padding: '6px',
                          borderRadius: '4px',
                          border: '1px solid var(--color-border)',
                          backgroundColor: 'var(--color-bg-card)',
                          color: 'var(--color-text-light)',
                          fontWeight: '500',
                          width: '160px',
                          boxSizing: 'border-box'
                        }}
                      >
                        <option value="PERMANENT">Permanent</option>
                        <option value="TEMPORARY">Temporary</option>
                      </select>
                    </td>
                    <td style={PART3_TD_VALUE}></td>
                  </tr>

                  {/* Start Date */}
                  <tr>
                    <td style={PART3_TD_LABEL}>Start Date</td>
                    <td style={PART3_TD_VALUE}>
                      <input
                        type="date"
                        value={candidate.start_date || ''}
                        onChange={handleStartDateChange}
                        onBlur={handleStartDateBlur}
                        className={isNext('p4:start_date') ? 'next-glow' : undefined}
                        style={{ padding: '6px', borderRadius: '4px', border: '1px solid var(--color-border)', width: '160px', boxSizing: 'border-box', backgroundColor: 'var(--color-bg-card)', color: 'var(--color-text-light)', fontWeight: '500' }}
                      />
                      {/* The picker above is drawn in the browser's own language (a US-English
                          browser shows mm/dd/yyyy), so spell the date out unambiguously. */}
                      {formatDateLong(candidate.start_date) && (
                        <span style={{ marginLeft: '10px', fontSize: '13px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                          {formatDateLong(candidate.start_date)}
                        </span>
                      )}
                    </td>
                    <td style={PART3_TD_VALUE}>
                      {candidate.claire_notified_date && (
                        <span style={{ fontSize: '11px', color: PART3_COLOR, fontWeight: '600' }}>
                          ✓ Claire Hopkins emailed
                        </span>
                      )}
                    </td>
                  </tr>

                  {/* End Date (Temporary only) */}
                  {candidate.employment_type === 'TEMPORARY' && (
                    <tr>
                      <td style={PART3_TD_LABEL}>End Date (Optional)</td>
                      <td style={PART3_TD_VALUE}>
                        <input
                          type="date"
                          value={candidate.end_date || ''}
                          onChange={(e) => setCandidate(prev => ({ ...prev, end_date: e.target.value }))}
                          onBlur={(e) => {
                            const newDate = e.target.value;

                            // For TEMPORARY positions with an end date, trigger Claire notification modal
                            if (newDate && newDate.length === 10) {
                              // If Claire hasn't been notified yet and we have a start date, show confirmation modal
                              if (!candidate.claire_notified_date && candidate.start_date) {
                                setPendingStartDate(candidate.start_date);
                                setShowClaireConfirmModal(true);
                              } else {
                                // Otherwise just update the end date
                                handleChecklistUpdate('end_date', newDate);
                              }
                            } else if (!newDate) {
                              handleChecklistUpdate('end_date', null);
                            }
                          }}
                          style={{
                            padding: '6px',
                            borderRadius: '4px',
                            border: '1px solid var(--color-border)',
                            width: '160px',
                            backgroundColor: 'var(--color-bg-card)',
                            color: 'var(--color-text-light)',
                            fontWeight: '500'
                          }}
                        />
                      </td>
                      <td style={PART3_TD_VALUE}>
                        <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                          (Leave blank if not yet confirmed)
                        </span>
                      </td>
                    </tr>
                  )}

                  {/* Probation End Date (Auto-calculated) */}
                  {candidate.probation_end_date && (
                    <tr>
                      <td style={PART3_TD_LABEL}>Probation End Date</td>
                      <td style={PART3_TD_VALUE}>
                        <span style={{ fontSize: '16px', color: '#9b4fc4', fontWeight: '500' }}>
                          {new Date(candidate.probation_end_date + 'T00:00:00').toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </span>
                      </td>
                      <td style={PART3_TD_VALUE}>
                        {candidate.probation_calendar_event_id && (
                          <span style={{ fontSize: '11px', color: PART3_COLOR, fontWeight: '600' }}>
                            ✓ Calendar event created
                          </span>
                        )}
                      </td>
                    </tr>
                  )}

                  {/* Holiday Allowance (Pro-rata) */}
                  {candidate.start_date && candidate.holiday_allowance !== null && (
                    <tr>
                      <td style={PART3_TD_LABEL}>Holiday Allowance (Pro-rata)</td>
                      <td style={PART3_TD_VALUE}>
                        <span style={{ fontSize: '16px', color: '#9b4fc4', fontWeight: '500' }}>
                          {candidate.holiday_allowance_hours && candidate.holiday_allowance
                            ? `${candidate.holiday_allowance_hours} hours (${candidate.holiday_allowance} days)`
                            : `${candidate.holiday_allowance} days`}
                        </span>
                      </td>
                      <td style={PART3_TD_VALUE}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Step 2: Generate Contract & Request eSignature */}
            <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'var(--color-bg-hover)', borderRadius: '8px' }}>
              {/* Top-aligned, not centred: the right-hand column grows (generated
                  date, SignWell status, copy-link button) and centring would float
                  the heading down away from the button it belongs to. */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                <h3 style={{ margin: '8px 0 0 0', color: 'var(--color-text-light)' }}>
                  {usingSignWell ? 'Step 2: Generate contract and send for signature' : 'Step 2: Generate contract and request eSignature'}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                  <button
                    onClick={handleGenerateContract}
                    className={`btn-primary${glowIf('p4:generate')}`}
                    disabled={!candidate.contract_sent_date || generatingContract}
                    style={{
                      opacity: (!candidate.contract_sent_date || generatingContract) ? 0.5 : 1,
                      cursor: generatingContract ? 'not-allowed' : undefined
                    }}
                  >
                    {generatingContract
                      ? (usingSignWell ? 'Generating & sending...' : 'Generating...')
                      : candidate.contract_generated_date
                        ? (usingSignWell && candidate.contract_esign_document_id
                          ? <ResendLabel label="✓ Contract sent for signature" />
                          : <ResendLabel label="✓ Contract Generated" again="Generate again" />)
                        : (usingSignWell ? 'Generate & Send for Signature' : 'Generate Contract')}
                  </button>
                  {candidate.contract_generated_date && (
                    <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
                      Generated: {formatDateTime(candidate.contract_generated_date)}
                    </span>
                  )}
                  {candidate.contract_esign_document_id && (
                    <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
                      SignWell: {candidate.contract_esign_status || 'sent'}
                      {['Declined', 'Canceled', 'Expired', 'Bounced', 'Error'].includes(candidate.contract_esign_status) &&
                        ' — no signature will arrive; regenerate to send a new request'}
                    </span>
                  )}
                  {candidate.contract_esign_document_id && !candidate.contract_signed_date && (
                    <button
                      type="button"
                      onClick={() => handleCopyEsignLink('contract')}
                      disabled={copyingEsignLink === 'contract'}
                      className="btn-success"
                      title="If the SignWell email went to junk: copies the signing link so you can send it yourself"
                      style={{ fontSize: '12px', padding: '4px 10px' }}
                    >
                      {copyingEsignLink === 'contract' ? 'Fetching…' : '🔗 Copy signing link'}
                    </button>
                  )}
                </div>
              </div>

              {/* SignWell test mode: nothing was emailed, so the direct links from
                  the last Generate are the only way to walk the signing flow. */}
              {usingSignWell && signwellTestMode && candidate.contract_esign_document_id && !candidate.contract_signed_date && (
                <div style={{ marginTop: '10px', padding: '10px 12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '4px', border: '1px dashed var(--wylie-orange, #e8a33d)', fontSize: '13px' }}>
                  <strong>SignWell TEST MODE</strong> — no signing email was sent.{' '}
                  {signwellSigningUrls ? (
                    <>
                      Sign here:{' '}
                      {signwellSigningUrls.candidate && <a href={signwellSigningUrls.candidate} target="_blank" rel="noreferrer">candidate</a>}
                      {signwellSigningUrls.candidate && signwellSigningUrls.employer && ' · '}
                      {signwellSigningUrls.employer && <a href={signwellSigningUrls.employer} target="_blank" rel="noreferrer">employer</a>}
                      {' '}(candidate signs first).
                    </>
                  ) : (
                    <>The signing links are only returned at generation time — regenerate to get fresh ones, or take them from the backend log.</>
                  )}
                </div>
              )}

              {candidate.contract_generated_date && (
                <>

                  {!candidate.candidate_signed_date && candidate.contract_pdf_url && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                      <button
                        onClick={() => openCandidateDocument('contract', candidate.contract_pdf_url)}
                        className="btn-blue"
                      >
                        View Contract (Unsigned)
                      </button>
                    </div>
                  )}

                  {candidate.contract_signed_date ? (
                    <div style={{ marginTop: '15px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '4px', border: '2px solid var(--wylie-green)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <strong style={{ color: '#5cb85c' }}>✓ Contract fully executed</strong>
                          <AccountsSentTick notifiedDate={candidate.accounts_notified_date} accountsName={accountsName} />
                        </div>
                        {candidate.contract_signed_pdf_url && (
                          <button
                            onClick={() => openCandidateDocument('signed_contract', candidate.contract_signed_pdf_url)}
                            className="btn-success"
                          >
                            View Signed Contract (PDF)
                          </button>
                        )}
                      </div>
                      <p style={{ marginTop: '5px', fontSize: '14px', color: 'var(--color-text-muted)' }}>
                        Candidate signed: {candidate.candidate_signed_date ? formatDateTime(candidate.candidate_signed_date) : '—'}<br />
                        All parties signed: {formatDateTime(candidate.contract_signed_date)}
                      </p>
                    </div>
                  ) : candidate.candidate_signed_date ? (
                    <div style={{ marginTop: '15px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '4px', border: '2px solid var(--wylie-green)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong className={isWaiting('p4:countersign') ? 'next-glow-text' : undefined} style={{ color: '#5cb85c' }}>✓ Candidate signed contract — awaiting employer countersignature</strong>
                        <button
                          onClick={handleMarkFullyExecuted}
                          className="btn-secondary"
                          style={{ fontSize: '13px' }}
                        >
                          Mark fully executed (employer signed)
                        </button>
                      </div>
                      <p style={{ marginTop: '5px', fontSize: '14px', color: 'var(--color-text-muted)' }}>
                        Signed by candidate: {formatDateTime(candidate.candidate_signed_date)}. Onboarding (Part 5) is unlocked.
                      </p>
                    </div>
                  ) : candidate.contract_sent_date && (
                    <div style={{ marginTop: '15px', padding: '12px', backgroundColor: 'var(--color-bg-card)', borderRadius: '4px', border: '1px solid var(--wylie-green)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong className={isWaiting('p4:signature') ? 'next-glow-text' : undefined} style={{ color: '#5cb85c' }}>⏳ Awaiting Signature</strong>
                        <button
                          onClick={handleMarkCandidateSigned}
                          className="btn-secondary"
                          style={{ fontSize: '13px' }}
                        >
                          Mark candidate as signed (manual)
                        </button>
                      </div>
                      <p style={{ marginTop: '5px', fontSize: '14px', color: 'var(--color-text-muted)' }}>
                        {candidate.contract_esign_document_id
                          ? 'The signing request was sent through SignWell; the system checks SignWell every 15 minutes. Use the button if the candidate has signed but it wasn\'t detected automatically.'
                          : 'System is monitoring for eSignature completion. Use the button if the candidate has signed but it wasn\'t detected automatically.'}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Step 3 removed - Claire is now automatically notified when contract is generated (Step 2) */}
          </>
          )}
          </div>
        </div>

        {/* PART 5: Pre-Start Onboarding Checklist */}
        {candidate.candidate_signed_date && (
        <div className="detail-section">
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'var(--color-bg-hover)', borderRadius: '8px', border: `2px solid #9b4fc4`, '--glow-color': '#9b4fc4' }}>
                <h2 onClick={() => toggleSection('onboarding')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (expandedSections.onboarding !== false && (expandedSections.onboarding === true || onboardingData?.stats.completion_percentage !== 100)) ? '15px' : '0', color: '#9b4fc4', fontWeight: 'bold' }}>
                  <span>PART 5 - Onboarding Checklist</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {onboardingData?.stats.completion_percentage === 100 && (
                      <span style={{ fontWeight: '600', color: '#9b4fc4' }}>Completed</span>
                    )}
                    <span className="section-toggle-arrow">{(expandedSections.onboarding !== false && (expandedSections.onboarding === true || onboardingData?.stats.completion_percentage !== 100)) ? '▼' : '▶'}</span>
                  </div>
                </h2>

                {(expandedSections.onboarding !== false && (expandedSections.onboarding === true || onboardingData?.stats.completion_percentage !== 100)) && (onboardingLoading ? (
                  <p>Loading onboarding checklist...</p>
                ) : onboardingData ? (
                  <>
                    {/* Progress Stats */}
                    <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #9b4fc4' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <input
                            type="checkbox"
                            checked={onboardingData.stats.completion_percentage === 100}
                            onChange={handleToggleAllTasks}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                              accentColor: '#FBFFB9'
                            }}
                            title="Toggle all tasks"
                          />
                          <strong style={{ color: '#9b4fc4' }}>
                            Progress: {onboardingData.stats.completed_tasks} / {onboardingData.stats.total_tasks} tasks
                          </strong>
                        </div>
                        <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#9b4fc4' }}>
                          {onboardingData.stats.completion_percentage}%
                        </span>
                      </div>
                      <div style={{ width: '100%', height: '10px', backgroundColor: '#ddd', borderRadius: '5px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${onboardingData.stats.completion_percentage}%`,
                          height: '100%',
                          backgroundColor: '#9b4fc4',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>

                    {/* Tasks in API order */}
                    {onboardingData.tasks.map(task => {
                              const taskData = onboardingData.checklist[task.key] || {};
                              const isCompleted = taskData.completed || false;
                              const isExpanded = expandedCompletedTasks[task.key] || false;

                              return (
                                <div
                                  key={task.key}
                                  className={isNext(`p5:${task.key}`) ? 'next-glow' : undefined}
                                  style={{
                                    marginBottom: '12px',
                                    padding: '12px',
                                    backgroundColor: 'var(--color-bg-card)',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '12px'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isCompleted}
                                    onChange={(e) => handleOnboardingTaskUpdate(task.key, e.target.checked, taskData.notes || '')}
                                    style={{
                                      marginTop: '4px',
                                      width: '18px',
                                      height: '18px',
                                      cursor: 'pointer',
                                      accentColor: '#FBFFB9'
                                    }}
                                  />
                                  <div style={{ flex: 1 }}>
                                    {/* Always visible: Label row with expand/collapse toggle */}
                                    <div
                                      onClick={() => {
                                        if (isCompleted) {
                                          setExpandedCompletedTasks(prev => ({
                                            ...prev,
                                            [task.key]: !prev[task.key]
                                          }));
                                        }
                                      }}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: isCompleted ? 'pointer' : 'default',
                                        marginBottom: (!isCompleted || isExpanded) ? '4px' : '0'
                                      }}
                                    >
                                      <div style={{
                                        fontWeight: '600',
                                        color: '#9b4fc4',
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                      }}>
                                        <span>{task.label}</span>
                                        {/* Staff notification indicators */}
                                        {task.key === 'google_account_created' && candidate.it_admin_notified_date && (
                                          <span style={{ fontSize: '12px', color: '#5cb85c', whiteSpace: 'nowrap' }}>✓ Pascal emailed</span>
                                        )}
                                        {task.key === 'uniform_ordered' && candidate.uniform_notified_date && (
                                          <span style={{ fontSize: '12px', color: '#5cb85c', whiteSpace: 'nowrap' }}>✓ Codie emailed</span>
                                        )}
                                        {task.key === 'radiation_monitor_arranged' && candidate.health_safety_notified_date && (
                                          <span style={{ fontSize: '12px', color: '#5cb85c', whiteSpace: 'nowrap' }}>✓ Ellie/Codie/Lauren emailed</span>
                                        )}
                                        {task.key === 'gdpr_course_enrollment' && onboardingData.checklist[task.key]?.completed && (
                                          <span style={{ fontSize: '12px', color: '#5cb85c', whiteSpace: 'nowrap' }}>✓ IHT emailed</span>
                                        )}
                                      </div>

                                      {/* Go to buttons - always visible on title row */}
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {task.key === 'vds_policy_added' && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); window.open('https://www.thevds.co.uk/login', '_blank'); }}
                                            className="btn-success"
                                            style={{ padding: '6px 12px', fontSize: '13px', fontWeight: '500' }}
                                          >
                                            Go to VDS Add Person
                                          </button>
                                        )}

                                        {(task.key === 'ezyvet_login_created' || task.key === 'ezyvet_message_groups' || task.key === 'ezyvet_written_rx' || task.key === 'staff_notified' || task.key === 'ezyvet_appt_comm_groups') && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); window.open('https://wylievets.euw1.ezyvet.com/login.php', '_blank'); }}
                                            className="btn-success"
                                            style={{ padding: '6px 12px', fontSize: '13px', fontWeight: '500' }}
                                          >
                                            Go to Ezyvet
                                          </button>
                                        )}

                                        {task.key === 'edays_added' && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); window.open('https://wylievets.e-days.co.uk/', '_blank'); }}
                                            className="btn-success"
                                            style={{ padding: '6px 12px', fontSize: '13px', fontWeight: '500' }}
                                          >
                                            Go to E-days
                                          </button>
                                        )}

                                        {task.key === 'gdpr_course_enrollment' && (
                                          <button
                                            type="button"
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              if (onboardingData.checklist[task.key]?.completed) return;
                                              try {
                                                await candidateAPI.api.post(`/api/candidates/${id}/send-gdpr-enrollment`);
                                                showAlert('GDPR enrollment email is being sent to IHT Consultancy!', 'success');
                                                // Reload onboarding data to update checklist status
                                                setTimeout(() => loadOnboardingData(), 1000);
                                              } catch (error) {
                                                console.error('Error sending GDPR enrollment email:', error);
                                                showAlert('Failed to send GDPR enrollment email', 'error');
                                              }
                                            }}
                                            className="btn-success"
                                            style={{
                                              padding: '6px 12px',
                                              fontSize: '13px',
                                              fontWeight: '500',
                                              // Sent is a done state, not an unavailable one: no greying out.
                                              ...(onboardingData.checklist[task.key]?.completed && { cursor: 'default', opacity: 1 })
                                            }}
                                            disabled={onboardingData.checklist[task.key]?.completed}
                                          >
                                            {onboardingData.checklist[task.key]?.completed ? '✓ Sent email to IHT' : 'Send Email to IHT'}
                                          </button>
                                        )}

                                        {task.key === 'unum_enrollment' && (
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              const portal = window.open('https://account.unum.co.uk/am/XUI/?realm=/alpha&goto=https%3A%2F%2Faccount.unum.co.uk%3A443%2Fam%2Foauth2%2Fauthorize%3Fclient_id%3Dc4965989-ed5f-46e6-8c7c-b53778cbd748%26redirect_uri%3Dhttps%3A%2F%2Femployer.unum.co.uk%26response_type%3Dcode%26scope%3Dopenid%2520email%2520profile%2520urn%3Aciamuk-v1%3Ascope%3Aemployer%3Ashared%2520urn%3Aciamuk-v1%3Ascope%3Aemployer%3Acashplan%2520phone%2520jobtitle%26state%3DMTg3MjAxMTkxMjM0MTUyMjQ0NTg3MTExMjM5MjQyMjI4NjIxMjUyMTA%26code_challenge%3D6Dm0q-PMHCSCVhbvIRxJ86VITlTsahRdx2QZq-mVrTI%26code_challenge_method%3DS256&authIndexType=service&authIndexValue=Employer-Login#/', '_blank');
                                              const formPreview = window.open('about:blank', '_blank');
                                              if (!formPreview || !portal) {
                                                showAlert('Please allow popups for this site. Click the popup blocker icon in your address bar.', 'info');
                                              }
                                              if (formPreview) {
                                                try {
                                                  const token = await ensurePaneAuth();
                                                  formPreview.location = `${API_URL}/api/candidates/${id}/form-preview/unum?auth=${token}`;
                                                } catch (err) {
                                                  formPreview.close();
                                                  showAlert('Could not open the Unum form', 'error');
                                                }
                                              }
                                            }}
                                            className="btn-success"
                                            style={{ padding: '6px 12px', fontSize: '13px', fontWeight: '500' }}
                                          >
                                            Enroll in Unum (opens 2 tabs)
                                          </button>
                                        )}

                                        {task.key === 'name_badge_ordered' && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); window.open('https://www.namebadgesinternational.co.uk/user-access/login/', '_blank'); }}
                                            className="btn-success"
                                            style={{ padding: '6px 12px', fontSize: '13px', fontWeight: '500' }}
                                          >
                                            Go to Badge International
                                          </button>
                                        )}

                                        {task.key === 'door_plate_ordered' && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); window.open('https://badgemaster.co.uk/my-account/', '_blank'); }}
                                            className="btn-success"
                                            style={{ padding: '6px 12px', fontSize: '13px', fontWeight: '500' }}
                                          >
                                            Go to Badgemaster
                                          </button>
                                        )}

                                        {task.key === 'uniform_ordered' && (
                                          <>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); window.open('https://grahamegardner.co.uk/Account/Login', '_blank'); }}
                                              className="btn-success"
                                              style={{ padding: '6px 12px', fontSize: '13px', fontWeight: '500' }}
                                            >
                                              Go to Grahame Gardner
                                            </button>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); window.open('https://www.simonjersey.com/', '_blank'); }}
                                              className="btn-success"
                                              style={{ padding: '6px 12px', fontSize: '13px', fontWeight: '500' }}
                                            >
                                              Go to Simon Jersey
                                            </button>
                                          </>
                                        )}

                                        {/* The collapse triangle shows on completed tasks only, but
                                            its slot is always kept (hidden), so every task's green
                                            buttons end at the same right edge. */}
                                        <span
                                          className="section-toggle-arrow"
                                          aria-hidden={!isCompleted}
                                          style={{
                                            marginLeft: '8px',
                                            width: '18px',
                                            textAlign: 'center',
                                            visibility: isCompleted ? 'visible' : 'hidden'
                                          }}
                                        >
                                          {isExpanded ? '▼' : '▶'}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Conditionally visible: Details section */}
                                    {(!isCompleted || isExpanded) && (
                                      <>
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: '12px'
                                      }}>
                                        {/* Left column: description, note */}
                                        <div style={{ flex: 1 }}>
                                          <div style={{
                                            fontSize: '13px',
                                            color: 'var(--color-text-muted)',
                                            marginBottom: '4px'
                                          }}>
                                            {task.description}
                                          </div>
                                          {task.note && (
                                            <div style={{
                                              fontSize: '12px',
                                              color: '#288bcd',
                                              fontStyle: 'italic'
                                            }}>
                                              {task.note}
                                            </div>
                                          )}
                                        </div>

                                      {/* Right column: RCVS badges (for VDS task only) */}
                                      {task.key === 'vds_policy_added' && candidate.rcvs_number && (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                          <div style={{
                                            padding: '6px 12px',
                                            fontSize: '13px',
                                            backgroundColor: 'var(--wylie-coral, #f05f57)',
                                            color: 'white',
                                            borderRadius: '4px',
                                            fontWeight: '500'
                                          }}>
                                            RCVS: {candidate.rcvs_number}
                                          </div>
                                          {candidate.rcvs_verified && (
                                            <div style={{
                                              padding: '6px 12px',
                                              fontSize: '13px',
                                              backgroundColor: 'var(--wylie-coral, #f05f57)',
                                              color: 'white',
                                              borderRadius: '4px',
                                              fontWeight: '500'
                                            }}>
                                              ✓ RCVS Verified{candidate.rcvs_verified_date && ` on ${formatDate(candidate.rcvs_verified_date, {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                              })}`}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    {/* Completion date shown in expanded view */}
                                    {isCompleted && taskData.completed_date && (
                                      <div style={{
                                        fontSize: '12px',
                                        color: 'white',
                                        marginTop: '6px'
                                      }}>
                                        ✓ Completed {formatDate(taskData.completed_date)}
                                        {taskData.completed_by && ` by ${taskData.completed_by}`}
                                      </div>
                                    )}
                                    </>
                                  )}
                              </div>
                            </div>
                              );
                    })}

                    {/* Completion Message */}
                    {onboardingData.stats.completion_percentage === 100 && (
                      <div style={{
                        marginTop: '20px',
                        padding: '15px',
                        backgroundColor: '#1c5f36',
                        border: '2px solid #5cb85c',
                        borderRadius: '6px',
                        textAlign: 'center'
                      }}>
                        <strong style={{ color: 'white', fontSize: '16px' }}>
                          🎉 All onboarding tasks completed! Ready for {candidate.first_name}'s start date.
                        </strong>
                      </div>
                    )}
                  </>
                ) : (
                  <p style={{ color: 'var(--color-text-muted)' }}>
                    Contract must be signed to access onboarding checklist.
                  </p>
                ))}
          </div>
        </div>
        )}

        {/* PART 6: Induction Folder */}
        {candidate.candidate_signed_date && (
        <div className="detail-section">
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'var(--color-bg-hover)', borderRadius: '8px', border: '2px solid #dc2565', '--glow-color': '#dc2565' }}>
            <h2 onClick={() => toggleSection('inductionFolder')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (expandedSections.inductionFolder !== false && (expandedSections.inductionFolder === true || !inductionSettled)) ? '15px' : '0', color: '#f06292', fontWeight: 'bold' }}>
              <span>PART 6 - Induction Folder</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {inductionData?.completed && (
                  <span style={{ fontWeight: '600', color: '#f06292' }}>
                    {pendingTrainingLog
                      ? `${isProgression ? '' : 'Policies signed · '}${pendingTrainingLog.status?.signature_requested_date
                        ? 'Training Log awaiting signature'
                        : 'Training Log in progress'}`
                      : 'Completed'}
                  </span>
                )}
                <span className="section-toggle-arrow">{(expandedSections.inductionFolder !== false && (expandedSections.inductionFolder === true || !inductionSettled)) ? '▼' : '▶'}</span>
              </div>
            </h2>

            {(expandedSections.inductionFolder !== false && (expandedSections.inductionFolder === true || !inductionSettled)) && (inductionLoading ? (
              <p>Loading induction folder...</p>
            ) : inductionData?.visible ? (
              <>
                {!isProgression && (<>
                {/* Work Location Selector - Always visible */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #dc2565' }}>
                  <p style={{ marginBottom: '10px', fontWeight: '500', color: '#f06292' }}>
                    {inductionData.work_location ? 'Work Location (change if needed):' : 'Please set work location to see applicable documents:'}
                  </p>
                  <select
                    className={isNext('p6:location') ? 'next-glow' : undefined}
                    value={candidate.work_location || ''}
                    onChange={async (e) => {
                      const newLocation = e.target.value;
                      try {
                        await candidateAPI.update(id, { work_location: newLocation });
                        const updated = await candidateAPI.get(id);
                        setCandidate(updated);
                        await loadInductionData();
                        showAlert('Work location updated. H&S summary will be updated', 'info');
                      } catch (error) {
                        console.error('Error updating location:', error);
                        showAlert('Failed to update location', 'error');
                      }
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #dc2565',
                      fontSize: '14px',
                      fontWeight: '500',
                      minWidth: '150px',
                      backgroundColor: 'var(--color-bg-card)',
                      color: 'var(--color-text-light)',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select Location</option>
                    <option value="brentwood">Brentwood</option>
                    <option value="upminster">Upminster</option>
                  </select>
                </div>

                {/* Progress Bar */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'var(--color-bg-card)', borderRadius: '6px', border: '1px solid #dc2565' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '500', color: '#f06292' }}>Progress:</span>
                    <span style={{ fontWeight: '600', color: '#f06292' }}>
                      {inductionProgress.signed} / {inductionProgress.total} documents signed ({inductionProgress.percent}%)
                    </span>
                  </div>
                  <div style={{ width: '100%', height: '12px', backgroundColor: 'var(--color-bg-hover)', borderRadius: '6px', overflow: 'hidden' }}>
                    <div style={{ width: `${inductionProgress.percent}%`, height: '100%', backgroundColor: '#f06292', transition: 'width 0.3s ease' }} />
                  </div>
                </div>

                {/* Batch Action Buttons */}
                <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                  {/* Button 1 and the list of what it sent sit side by side, so the
                      strip keeps its three-column shape rather than growing a tall
                      first column. */}
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <button
                      type="button"
                      onClick={handleSendInductionEmail}
                      disabled={inductionData.documents.filter(doc => doc.template_id && !doc.requires_esignature && doc.kind !== 'training_log' && !doc.status.generated_date).length > 0}
                      className={`btn-pink${glowIf('p6:email')}`}
                      style={{ fontWeight: '600' }}
                    >
                      {inductionData.documents.some(doc => !doc.requires_esignature && doc.status.sent_date)
                        ? <ResendLabel label="1. Induction Email sent" again="1. Send again" />
                        : '1. Send Induction Email'}
                    </button>
                    <SendStatusSubtitle
                      sentDate={candidate.induction_email_sent_date}
                      failedDate={candidate.induction_email_send_failed_date}
                      attemptedDate={candidate.induction_email_send_attempted_date}
                    />
                  </div>

                  {/* What the induction email actually carried, beside the button that
                      sent it. Built from the candidate's own document list rather than
                      a fixed set of names, because the attachments are position-specific
                      (the Ezyvet log differs for nurses, vets and reception, and an
                      office starter gets none) — a hardcoded list like Part 3's would be
                      wrong for someone. Stacked one per line because the longest label
                      runs to ~35 characters; Part 3 can lay its four out in a row only
                      because that line spans the full panel width. */}
                  {candidate.induction_email_sent_date && (
                    <div style={{ maxWidth: '280px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '13px' }}>Attached:</span>
                      {inductionData.documents
                        .filter(doc => !doc.requires_esignature && doc.kind !== 'training_log')
                        .map((doc) => (
                          <div key={doc.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '13px', marginTop: '2px' }}>
                            <span style={{ color: '#16a34a' }}>&#10003;</span>
                            <span style={{ color: 'var(--color-text-primary)' }}>{doc.label}</span>
                          </div>
                        ))}
                    </div>
                  )}
                  </div>
                  {usingSignWell ? (() => {
                    const esigDocs = inductionData.documents.filter(doc => doc.requires_esignature);
                    const unsigned = esigDocs.filter(doc => !doc.status.signed_date);
                    const signed = esigDocs.length - unsigned.length;
                    const bundle = inductionData.esign_bundle;
                    const failed = ['Declined', 'Canceled', 'Expired', 'Bounced', 'Error'].includes(bundle?.status);
                    const allSent = unsigned.length > 0 && unsigned.every(doc => doc.status.esign_document_id) && !failed;
                    // No location = no H&S summary in the set; sending now would need a
                    // second request later, so the button waits for the location.
                    const noLocation = !inductionData.work_location;
                    const disabled = sendingInductionEsign || unsigned.length === 0 || allSent || noLocation;
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: '280px' }}>
                        <button
                          onClick={handleSendInductionEsignatures}
                          disabled={disabled}
                          className={`btn-pink${glowIf('p6:esig')}`}
                          style={{ fontWeight: '600' }}
                        >
                          {sendingInductionEsign
                            ? 'Generating & sending…'
                            : unsigned.length === 0
                              ? '2. eSignature Docs signed'
                              : allSent
                                ? '2. eSignature Docs sent'
                                : `2. Send eSignature Docs (${unsigned.length})`}
                        </button>
                        <span className={isWaiting('p6:esig') ? 'next-glow-text next-glow-tint' : undefined} style={{ ...SEND_STATUS_BASE, color: (failed || (noLocation && !bundle)) ? '#e74c3c' : 'var(--color-text-muted)' }}>
                          {bundle
                            ? `Sent via SignWell · ${signed} of ${esigDocs.length} signed · status: ${bundle.status || 'sent'}${failed ? ' — re-send needed' : ''}`
                            : noLocation
                              ? 'Set work location 1st'
                              : 'Generates the documents and emails them to the candidate as ONE SignWell request — the system then checks SignWell every 15 minutes'}
                        </span>
                        {signwellTestMode && bundle?.signing_url && unsigned.length > 0 && (
                          <span style={{ ...SEND_STATUS_BASE, color: 'var(--color-text-muted)' }}>
                            TEST MODE — no email sent: <a href={bundle.signing_url} target="_blank" rel="noreferrer">sign as the candidate</a>
                          </span>
                        )}
                        {bundle && unsigned.length > 0 && !failed && (
                          <button
                            type="button"
                            onClick={() => handleCopyEsignLink('induction')}
                            disabled={copyingEsignLink === 'induction'}
                            className="btn-success"
                            title="If the SignWell email went to junk: copies the signing link so you can send it yourself"
                            style={{ fontSize: '12px', padding: '4px 10px', alignSelf: 'flex-start' }}
                          >
                            {copyingEsignLink === 'induction' ? 'Fetching…' : '🔗 Copy signing link'}
                          </button>
                        )}
                      </div>
                    );
                  })() : (
                  <div className="esig-gen-tip-wrap" style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative', maxWidth: '260px' }}>
                  {inductionData.documents.filter(doc => doc.template_id && !doc.status.generated_date && doc.requires_esignature).length > 0 && (
                    <span className="esig-gen-tip">
                      Opens {inductionData.documents.filter(doc => doc.template_id && !doc.status.generated_date && doc.requires_esignature).length} new tab{inductionData.documents.filter(doc => doc.template_id && !doc.status.generated_date && doc.requires_esignature).length === 1 ? '' : 's'} for you to request eSignatures in Google Docs — reception of the signed docs will then be monitored
                    </span>
                  )}
                  <button
                    onClick={handleGenerateAllInductionDocs}
                    disabled={inductionData.documents.filter(doc => doc.template_id && !doc.status.generated_date && doc.requires_esignature).length === 0}
                    className="btn-pink"
                    style={{ fontWeight: '600' }}
                  >
                    {inductionData.documents.filter(doc => doc.template_id && !doc.status.generated_date && doc.requires_esignature).length === 0
                      ? '2. eSignature Docs generated'
                      : `2. Generate All eSignature Docs (${inductionData.documents.filter(doc => doc.template_id && !doc.status.generated_date && doc.requires_esignature).length})`}
                  </button>
                  {(() => {
                    const esigDocs = inductionData.documents.filter(doc => doc.template_id && doc.requires_esignature);
                    const generated = esigDocs.filter(doc => doc.status.generated_date).length;
                    const sent = esigDocs.filter(doc => doc.status.sent_date).length;
                    const misdirected = esigDocs.filter(doc => doc.status.request_state === 'misdirected').length;
                    const total = esigDocs.length;
                    // "Generated" and "sent" are separate facts: the document is created
                    // by the app, the eSignature request is raised by hand in Drive
                    // afterwards. Showing only the first is what hid the 18/08/2026 miss.
                    return (
                      <>
                        <span style={{ ...SEND_STATUS_BASE, color: misdirected > 0 ? '#e74c3c' : 'var(--color-text-muted)' }}>
                          {generated === 0
                            ? 'Not yet generated'
                            : `${generated === total ? 'All' : generated + ' of ' + total} generated · ${sent} of ${total} request${total === 1 ? '' : 's'} sent`}
                          {misdirected > 0 && ` · ⚠ ${misdirected} to the wrong recipient`}
                        </span>
                        <button
                          onClick={handleVerifyInductionRequests}
                          disabled={verifyingInductionRequests || generated === 0}
                          className="btn-blue"
                          style={{ marginTop: '8px', padding: '8px 16px', fontWeight: '600' }}
                        >
                          {verifyingInductionRequests ? 'Checking…' : '3. Check requests sent'}
                        </button>
                        <span style={{ ...SEND_STATUS_BASE, color: 'var(--color-text-muted)' }}>
                          Asks Google Drive whether each eSignature request was really launched, and who it reached — also runs automatically every 15 minutes
                        </span>
                      </>
                    );
                  })()}
                  </div>
                  )}
                </div>

                </>)}
                {/* Document Cards */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ marginBottom: '15px', color: '#f06292' }}>
                    {isProgression ? 'Training' : 'Induction Documents'} ({inductionData.documents.length})
                  </h3>
                  {orderedInductionDocs.map((doc) => (
                    <div
                      key={doc.key}
                      // The Training Log glows while it is still open: it is the one row
                      // HR keeps coming back to for weeks, and the others go quiet.
                      className={doc.kind === 'training_log' && (isNext('p6:training_log') || isWaiting('p6:training_log')) ? 'next-glow' : undefined}
                      style={{
                        marginBottom: '12px',
                        padding: '15px',
                        backgroundColor: 'var(--color-bg-card)',
                        borderRadius: '6px',
                        border: '1px solid #dc2565',
                        opacity: doc.status.signed_date ? 0.8 : 1
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{
                            margin: '0 0 5px 0',
                            color: doc.status.signed_date ? '#666' : '#E3D0D8'
                          }}>
                            {/* Display only: the stored label is load-bearing (generated
                                Doc names, email attachment names, request-PDF matching). */}
                            {/acknowledg/i.test(doc.description || '') ? `${doc.label} — Acknowledgment` : doc.label}
                            {doc.requires_esignature && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#f06292' }}>eSignature</span>}
                            {doc.kind === 'training_log' && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#f06292' }}>filled in the app · signed at the end of training</span>}
                            {doc.status.signed_date ? (
                              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#5cb85c', fontWeight: '600', whiteSpace: 'nowrap' }}>✓ Signed</span>
                            ) : doc.status.sent_date && doc.status.request_state !== 'misdirected'
                              // A Training Log's sent_date can date from when it was an email
                              // attachment; only a signature request counts as sent for it.
                              && (doc.kind !== 'training_log' || doc.status.signature_requested_date)
                              && !(doc.status.esign_document_id && ['Declined', 'Canceled', 'Expired', 'Bounced', 'Error'].includes(doc.status.esign_status)) ? (
                              <span
                                title={doc.status.esign_document_id
                                  ? `Sent via SignWell to ${candidate.email} (${doc.status.esign_status || 'sent'})`
                                  : doc.status.request_recipients?.length
                                    ? `eSignature request sent to ${doc.status.request_recipients.join(', ')}`
                                    : 'Sent'}
                                style={{ marginLeft: '8px', fontSize: '12px', color: '#3498db', fontWeight: '600', whiteSpace: 'nowrap' }}
                              >
                                ✓ Sent
                              </span>
                            ) : null}
                          </h4>
                          {doc.note && (
                            <p style={{ margin: 0, fontSize: '12px', color: '#8C9DBA', fontStyle: 'italic' }}>
                              {doc.note}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {/* Action Buttons - Only for eSignature documents (under SignWell
                              the bundle button above generates and sends them together) */}
                          {!usingSignWell && doc.template_id && !doc.status.generated_date && doc.requires_esignature && (
                            <button
                              onClick={() => handleGenerateSingleInductionDoc(doc.key)}
                              disabled={inductionGeneratingDocs[doc.key]}
                              className="btn-pink"
                              style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                            >
                              {inductionGeneratingDocs[doc.key] ? 'Generating...' : 'Generate'}
                            </button>
                          )}

                          {doc.kind === 'training_log' && (
                            <button
                              onClick={() => setTrainingLogOpen(true)}
                              className={`btn-pink${glowIf('p6:training_log')}`}
                              style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                            >
                              {doc.status.signed_date ? 'View training log' : 'Open training log'}
                            </button>
                          )}

                          {/* Under Google eSignature, View is the way in to the manual request, so
                              every generated document gets one. Under SignWell the signing
                              documents and the Training Log are generated and sent in one go, so
                              there is nothing to do in the Doc (and the log's Doc is rebuilt on
                              every request, so edits made there are lost) — only the information
                              documents keep View, since they are what the induction email carries. */}
                          {doc.status.generated_date && doc.status.pdf_url && !(usingSignWell && (doc.requires_esignature || doc.kind === 'training_log')) && (
                            <button
                              onClick={() => openCandidateDocument('induction', doc.status.pdf_url, doc.key)}
                              className="btn-small btn-blue"
                            >
                              View
                            </button>
                          )}

                          {doc.status.signed_pdf_url && (
                            <a
                              href={doc.status.signed_pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                if (driveDirect) return;
                                e.preventDefault();
                                openCandidateDocument('induction_signed', doc.status.signed_pdf_url, doc.key);
                              }}
                                className="btn-success hover-swap"
                                style={{ padding: '6px 12px', textDecoration: 'none', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                            >
                              {/* Reads "Signed PDF" at rest and "View" under the pointer; both
                                  labels share one grid cell so the button never changes width. */}
                              <span className="hover-swap-rest">Signed PDF</span>
                              <span className="hover-swap-hover">View</span>
                            </a>
                          )}

                          {/* Status Badges */}
                          {doc.kind === 'training_log' && !doc.status.signed_date && !doc.status.signature_requested_date ? (
                            <span
                              title="Ticked off in the app as training progresses; sent for signature from the training log"
                              style={{ padding: '4px 12px', backgroundColor: inductionData.training_log?.started ? '#9b59b6' : '#95a5a6', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                            >
                              {inductionData.training_log?.started
                                ? `In progress · ${inductionData.training_log.progress.ticked_rows} / ${inductionData.training_log.progress.total_rows}`
                                : 'Not started'}
                            </span>
                          ) : doc.status.signed_date ? (
                            null /* "✓ Signed" sits beside the eSignature tag in the title */
                          ) : doc.status.request_state === 'misdirected' ? (
                            <span
                              title={`Request sent to ${(doc.status.request_recipients || []).join(', ') || 'nobody outside the practice'} — not ${candidate.email}`}
                              style={{ padding: '4px 12px', backgroundColor: '#e74c3c', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                            >
                              ⚠ Wrong recipient
                            </span>
                          ) : doc.status.esign_document_id && ['Declined', 'Canceled', 'Expired', 'Bounced', 'Error'].includes(doc.status.esign_status) ? (
                            <span
                              title={`SignWell request ${doc.status.esign_status.toLowerCase()} — re-send the eSignature documents`}
                              style={{ padding: '4px 12px', backgroundColor: '#e74c3c', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                            >
                              ⚠ {doc.status.esign_status}
                            </span>
                          ) : doc.status.sent_date ? (
                            null /* "✓ Sent" sits beside the tag in the title */
                          ) : (doc.requires_esignature || doc.status.signature_requested_date) && doc.status.generated_date ? (
                            <span
                              title="The document exists but no Google eSignature request has been raised for it yet"
                              style={{ padding: '4px 12px', backgroundColor: '#e67e22', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                            >
                              Request not sent
                            </span>
                          ) : doc.status.generated_date === 'info_only' ? (
                            <span style={{ padding: '4px 12px', backgroundColor: '#9b59b6', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                              Ready for Email
                            </span>
                          ) : doc.status.generated_date && !doc.requires_esignature ? (
                            <span style={{ padding: '4px 12px', backgroundColor: '#9b59b6', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                              Ready for Email
                            </span>
                          ) : doc.status.generated_date ? (
                            <span style={{ padding: '4px 12px', backgroundColor: '#dc2565', color: '#333', borderRadius: '4px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                              Generated
                            </span>
                          ) : (
                            <span style={{ padding: '4px 12px', backgroundColor: '#95a5a6', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                              Pending
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Completion Message */}
                {inductionData.completed && (
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: pendingTrainingLog ? '#9b59b6' : '#2ecc71',
                    borderRadius: '6px',
                    textAlign: 'center'
                  }}>
                    <strong style={{ color: 'white', fontSize: '16px' }}>
                      {pendingTrainingLog
                        ? `${isProgression ? '' : 'Policy documents all signed. '}${pendingTrainingLog.status?.signature_requested_date
                          ? 'The Training Log has been sent for signature.'
                          : 'The Training Log is still in progress and is signed at the end of training.'}`
                        : '🎉 Induction complete! All documents signed.'}
                    </strong>
                  </div>
                )}
              </>
            ) : (
              <p style={{ color: 'var(--color-text-muted)' }}>
                Contract must be signed to access induction folder.
              </p>
            ))}
          </div>
        </div>
        )}

        {emailAttach.open && (
          <div className="modal-overlay" onClick={closeEmailAttachModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '780px', width: '95%' }}>
              <div className="modal-header">
                <h2>Import from email</h2>
                <button className="modal-close" onClick={closeEmailAttachModal}>×</button>
              </div>
              <form className="schedule-form email-attach-form" onSubmit={(e) => { e.preventDefault(); runEmailAttachSearch(emailAttach.query); }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label htmlFor="email-attach-q" style={{ color: PART1A_COLOR }}>Emails from</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      id="email-attach-q"
                      type="text"
                      value={emailAttach.query}
                      onChange={(e) => setEmailAttach(prev => ({ ...prev, query: e.target.value }))}
                      placeholder="the candidate's or a recruiter's email address, or a name"
                      style={{ flex: 1 }}
                    />
                    <button type="submit" className="btn-primary btn-outline-yellow" disabled={emailAttach.loading}>
                      {emailAttach.loading ? 'Searching…' : 'Search'}
                    </button>
                  </div>
                </div>
              </form>
              <div className="email-attach-results">
                {emailAttach.errors.map((msg) => (
                  <div key={msg} className="email-attach-error">⚠ {msg}</div>
                ))}
                {!emailAttach.loading && emailAttach.results.length === 0 && (
                  <p className="email-attach-empty">
                    No document attachments found. Try the recruiter's address, or the candidate's name.
                  </p>
                )}
                {emailAttach.results.map((item) => {
                  const key = `${item.inbox}:${item.message_id}:${item.filename}`;
                  const busyKind = emailAttach.attaching && emailAttach.attaching.startsWith(`${key}:`)
                    ? emailAttach.attaching.slice(key.length + 1)
                    : null;
                  return (
                    <div key={key} className="email-attach-row">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: 'var(--color-text-light)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          📎 {item.filename} <span className="interview-step-detail">({formatBytes(item.size)})</span>
                        </div>
                        <div className="interview-step-detail" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.date ? formatDateTime(item.date) : ''} · {item.from} · {item.subject || '(no subject)'}
                        </div>
                        <div className="interview-step-detail">in {item.inbox}</div>
                      </div>
                      <div className="interview-step-actions">
                        <button
                          className="btn-primary"
                          style={{ padding: '6px 12px', fontSize: '13px' }}
                          onClick={() => handleAttachFromEmail(item, 'cv')}
                          disabled={!!emailAttach.attaching}
                        >
                          {busyKind === 'cv' ? 'Filing…' : 'as CV'}
                        </button>
                        <button
                          className="btn-secondary"
                          style={{ padding: '6px 12px', fontSize: '13px' }}
                          onClick={() => handleAttachFromEmail(item, 'cover_letter')}
                          disabled={!!emailAttach.attaching}
                        >
                          {busyKind === 'cover_letter' ? 'Filing…' : 'as cover letter'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {showScheduleModal && (
          <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  {scheduleType === 'phone-interview' && 'Add phone interview to calendar'}
                  {scheduleType === 'in-person-interview' && 'Add interview to calendar'}
                  {scheduleType === 'trial-day' && `Add ${trialLabel.toLowerCase()} to calendar`}
                </h2>
                <button
                  className="modal-close"
                  onClick={() => setShowScheduleModal(false)}
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleScheduleSubmit} className="schedule-form">
                <div className="form-group">
                  <label htmlFor="schedule-datetime">Date and Time *</label>
                  <input
                    type="datetime-local"
                    id="schedule-datetime"
                    value={scheduleDateTime}
                    onChange={(e) => setScheduleDateTime(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="schedule-duration">Duration (minutes) *</label>
                  <input
                    type="number"
                    id="schedule-duration"
                    value={scheduleDuration}
                    onChange={(e) => setScheduleDuration(e.target.value)}
                    min="15"
                    step="15"
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => setShowScheduleModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Add to calendar & invite
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showJobOfferModal && (
          <div className="modal-overlay" onClick={() => setShowJobOfferModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Job Offer Details</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowJobOfferModal(false)}
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSendOffer} className="schedule-form">
                <div className="form-group">
                  <label htmlFor="hours-per-week">Hours per Week</label>
                  {/* step="any": these inputs sit in a <form>, so without it the
                      browser's constraint validation blocks submission outright on a
                      part-time 22.5-hour week rather than just flagging it. */}
                  <input
                    type="number"
                    id="hours-per-week"
                    step="any"
                    value={jobOfferForm.hours_per_week}
                    onChange={(e) => handleJobOfferFormChange('hours_per_week', e.target.value)}
                    placeholder="e.g. 40"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="hours-per-shift">Hours per Shift / Day</label>
                  <input
                    type="number"
                    id="hours-per-shift"
                    step="any"
                    value={jobOfferForm.hours_per_shift}
                    onChange={(e) => handleJobOfferFormChange('hours_per_shift', e.target.value)}
                    placeholder="e.g. 8"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="annual-salary">Annual Salary</label>
                  <input
                    type="number"
                    id="annual-salary"
                    value={jobOfferForm.annual_salary}
                    onChange={(e) => handleJobOfferFormChange('annual_salary', e.target.value)}
                    placeholder="e.g. 45000"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="hourly-salary">Hourly Salary (auto-calculated)</label>
                  <input
                    type="number"
                    id="hourly-salary"
                    value={jobOfferForm.hourly_salary}
                    onChange={(e) => handleJobOfferFormChange('hourly_salary', e.target.value)}
                    placeholder="Calculated automatically"
                    step="0.01"
                    readOnly
                  />
                </div>
                {hoursOfWorkDefaults && (
                  <div className="form-group">
                    <label htmlFor="hours-of-work">Hours of work (as it will appear in the contract)</label>
                    <textarea
                      id="hours-of-work"
                      rows={10}
                      value={jobOfferForm.hours_of_work}
                      onChange={(e) => handleJobOfferFormChange('hours_of_work', e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '13px', lineHeight: 1.4 }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginTop: '4px', fontSize: '12px', color: 'var(--color-text-muted)' }}>
                      <span>
                        {jobOfferForm.hours_of_work_edited
                          ? "This candidate's own wording — it will not change if the hours above change."
                          : "Pre-filled with the standard wording for this position. Edit only if this candidate's shifts or weekends differ."}
                      </span>
                      {jobOfferForm.hours_of_work_edited && (
                        <button
                          type="button"
                          onClick={resetJobOfferHoursOfWork}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', color: 'inherit', whiteSpace: 'nowrap', fontSize: '12px' }}
                        >
                          Reset to standard
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => setShowJobOfferModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Send Job Offer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showClaireConfirmModal && (
          <div className="modal-overlay" onClick={handleDismissClaireModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
              maxWidth: '500px',
              backgroundColor: 'var(--color-bg-card)',
              padding: '30px',
              borderRadius: '10px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{ marginBottom: '20px' }}>
                <h2 style={{ margin: '0 0 20px 0', fontSize: '24px', fontWeight: '600' }}>
                  {candidate.employment_type === 'TEMPORARY' && candidate.end_date
                    ? 'Confirm start and end dates?'
                    : 'Confirm start date?'}
                </h2>
                <button
                  className="modal-close"
                  onClick={handleDismissClaireModal}
                  style={{ position: 'absolute', top: '20px', right: '20px' }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: '30px' }}>
                {/* Start date */}
                <div style={{ marginBottom: candidate.employment_type === 'TEMPORARY' && candidate.end_date ? '15px' : '0' }}>
                  {candidate.employment_type === 'TEMPORARY' && candidate.end_date && (
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--color-text-muted)' }}>
                      Start Date
                    </label>
                  )}
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: 'var(--color-text)',
                    padding: '12px 15px',
                    backgroundColor: 'var(--color-bg)',
                    borderRadius: '5px',
                    border: '1px solid var(--color-border)'
                  }}>
                    {new Date(pendingStartDate + 'T00:00:00').toLocaleDateString('en-GB', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>

                {/* End date (if temporary with end date) */}
                {candidate.employment_type === 'TEMPORARY' && candidate.end_date && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--color-text-muted)' }}>
                      End Date
                    </label>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '500',
                      color: 'var(--color-text)',
                      padding: '12px 15px',
                      backgroundColor: 'var(--color-bg)',
                      borderRadius: '5px',
                      border: '1px solid var(--color-border)'
                    }}>
                      {new Date(candidate.end_date + 'T00:00:00').toLocaleDateString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })} <span style={{ color: 'var(--color-text-muted)', fontSize: '16px' }}>(Fixed-term)</span>
                    </div>
                  </div>
                )}

                {/* Note for temporary without end date */}
                {candidate.employment_type === 'TEMPORARY' && !candidate.end_date && (
                  <p style={{ fontSize: '14px', fontStyle: 'italic', color: 'var(--color-text-muted)', marginTop: '10px', marginBottom: '0' }}>
                    (Temporary position - end date TBD)
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '15px' }}>
                <button
                  type="button"
                  onClick={handleCancelClaireNotification}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                >
                  Save without emailing Claire
                </button>
                <button
                  type="button"
                  onClick={handleConfirmClaireNotification}
                  className="btn-primary"
                  style={{ flex: 1 }}
                >
                  Save and email Claire
                </button>
              </div>
            </div>
          </div>
        )}

        {showNightWorkerModal && (
          <div className="modal-overlay" onClick={() => setShowNightWorkerModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
              <div className="modal-header">
                <h2>Night Worker Health Questionnaire - Complete Employer Assessment</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowNightWorkerModal(false)}
                >
                  ×
                </button>
              </div>

              <div style={{ height: 'calc(90vh - 120px)', overflow: 'hidden', padding: '0' }}>
                <iframe
                  src={paneUrl('night-worker-preview')}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'white'
                  }}
                  title="Night Worker Form - Complete Assessment"
                />
              </div>

              <div className="modal-actions" style={{ marginTop: '0', borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
                <button
                  onClick={() => setShowNightWorkerModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showMedicalModal && (
          <div className="modal-overlay" onClick={() => setShowMedicalModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
              <div className="modal-header">
                <h2>Medical Questionnaire - Submitted Form</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowMedicalModal(false)}
                >
                  ×
                </button>
              </div>

              <div style={{ height: 'calc(90vh - 120px)', overflow: 'hidden', padding: '0' }}>
                <iframe
                  src={paneUrl('form-preview/medical')}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'white'
                  }}
                  title="Medical Questionnaire Form"
                />
              </div>

              <div className="modal-actions" style={{ marginTop: '0', borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
                <button
                  onClick={() => setShowMedicalModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showHmrcModal && (
          <div className="modal-overlay" onClick={() => setShowHmrcModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
              <div className="modal-header">
                <h2>HMRC Starter Checklist - Submitted Form</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowHmrcModal(false)}
                >
                  ×
                </button>
              </div>

              <div style={{ height: 'calc(90vh - 120px)', overflow: 'hidden', padding: '0' }}>
                <iframe
                  src={paneUrl('form-preview/hmrc')}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'white'
                  }}
                  title="HMRC Starter Checklist Form"
                />
              </div>

              <div className="modal-actions" style={{ marginTop: '0', borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
                <button
                  onClick={() => setShowHmrcModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showPersonalRecordModal && (
          <div className="modal-overlay" onClick={() => setShowPersonalRecordModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
              <div className="modal-header">
                <h2>Personal Record Form - Submitted Form</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowPersonalRecordModal(false)}
                >
                  ×
                </button>
              </div>

              <div style={{ height: 'calc(90vh - 120px)', overflow: 'hidden', padding: '0' }}>
                <iframe
                  src={paneUrl('form-preview/personal_record')}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'white'
                  }}
                  title="Personal Record Form"
                />
              </div>

              <div className="modal-actions" style={{ marginTop: '0', borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
                <button
                  onClick={() => setShowPersonalRecordModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showUnumModal && (
          <div className="modal-overlay" onClick={() => setShowUnumModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
              <div className="modal-header">
                <h2>Unum Enrollment - Submitted Form</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowUnumModal(false)}
                >
                  ×
                </button>
              </div>

              <div style={{ height: 'calc(90vh - 120px)', overflow: 'hidden', padding: '0' }}>
                <iframe
                  src={paneUrl('form-preview/unum')}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'white'
                  }}
                  title="Unum Enrollment Form"
                />
              </div>

              <div className="modal-actions" style={{ marginTop: '0', borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
                <button
                  onClick={() => setShowUnumModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showIdDocumentModal && (
          <div className="modal-overlay" onClick={() => setShowIdDocumentModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
              <div className="modal-header">
                <h2>ID Document - {candidate.full_name}</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowIdDocumentModal(false)}
                >
                  ×
                </button>
              </div>

              <div style={{ height: 'calc(90vh - 120px)', overflow: 'hidden', padding: '0' }}>
                <CandidateDocumentFrame
                  candidateId={candidate.id}
                  kind="id_document"
                  fileId={candidate.id_document_file_id}
                  direct={driveDirect}
                  title="ID Document"
                />
              </div>

              <div className="modal-actions" style={{ marginTop: '0', borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
                <button
                  onClick={() => setShowIdDocumentModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showQualificationModal && (
          <div className="modal-overlay" onClick={() => setShowQualificationModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
              <div className="modal-header">
                <h2>Proof of Qualification - {candidate.full_name}</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowQualificationModal(false)}
                >
                  ×
                </button>
              </div>

              <div style={{ height: 'calc(90vh - 120px)', overflow: 'hidden', padding: '0' }}>
                <CandidateDocumentFrame
                  candidateId={candidate.id}
                  kind="qualification"
                  fileId={candidate.proof_of_qualification_file_id}
                  direct={driveDirect}
                  title="Proof of Qualification"
                />
              </div>

              <div className="modal-actions" style={{ marginTop: '0', borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
                <button
                  onClick={() => setShowQualificationModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showCpdRecordModal && (
          <div className="modal-overlay" onClick={() => setShowCpdRecordModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
              <div className="modal-header">
                <h2>CPD Record - {candidate.full_name}</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowCpdRecordModal(false)}
                >
                  ×
                </button>
              </div>

              <div style={{ height: 'calc(90vh - 120px)', overflow: 'hidden', padding: '0' }}>
                <CandidateDocumentFrame
                  candidateId={candidate.id}
                  kind="cpd_record"
                  fileId={candidate.cpd_record_file_id}
                  direct={driveDirect}
                  title="CPD Record"
                />
              </div>

              <div className="modal-actions" style={{ marginTop: '0', borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
                <button
                  onClick={() => setShowCpdRecordModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showRcvsResultsModal && (
          <div className="modal-overlay" onClick={() => setShowRcvsResultsModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', maxHeight: '90vh' }}>
              <div className="modal-header">
                <h2>RCVS Verification Results - {candidate.full_name}</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowRcvsResultsModal(false)}
                >
                  ×
                </button>
              </div>

              <div style={{ height: 'calc(90vh - 120px)', overflow: 'hidden', padding: '0' }}>
                <iframe
                  src={paneUrl('rcvs-results')}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'white'
                  }}
                  title="RCVS Verification Results"
                />
              </div>

              <div className="modal-actions" style={{ marginTop: '0', borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
                <button
                  onClick={() => setShowRcvsResultsModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Custom Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        buttonPosition={confirmDialog.buttonPosition}
        onConfirm={handleConfirmDialogConfirm}
        onCancel={handleConfirmDialogCancel}
        commentLabel={confirmDialog.commentLabel}
      />

      {/* Custom Alert Dialog */}
      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={handleAlertDialogClose}
      />

      {/* Email Preview Modal */}
      <EmailPreviewModal
        isOpen={emailPreviewModal.isOpen}
        emailData={emailPreviewModal.emailData}
        emailType={emailPreviewModal.emailType}
        title={emailPreviewModal.title}
        sendLabel={emailPreviewModal.sendLabel}
        cancelLabel={emailPreviewModal.cancelLabel}
        secondaryAction={emailPreviewModal.secondaryAction}
        note={emailPreviewModal.note}
        onSend={emailPreviewModal.onSendCallback}
        onCancel={() => setEmailPreviewModal({ isOpen: false, emailData: null, emailType: null, onSendCallback: null })}
      />

      {/* Part 6 Training Log */}
      <TrainingLogModal
        isOpen={trainingLogOpen}
        candidateId={id}
        candidateName={candidate ? `${candidate.first_name} ${candidate.last_name}` : ''}
        candidateEmail={candidate?.email}
        usingSignWell={usingSignWell}
        driveDirect={driveDirect}
        onClose={() => setTrainingLogOpen(false)}
        onChanged={loadInductionData}
        showConfirm={showConfirm}
        showAlert={showAlert}
        openCandidateDocument={openCandidateDocument}
        onCopySigningLink={handleCopyEsignLink}
      />
      </div>
    </div>
  );
}

export default CandidateDetail;
